import { MbscCalendarEvent } from '@mobiscroll/react';
import moment from 'moment';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { fetchAllEvents, fetchAllEventTypes } from 'core/API/events';
import { fetchHolidays } from 'core/API/holidays';
import { getDays, getOrganizations } from 'core/API/organizationSettings';
import {
	PutOneShift,
	fetchAllShifts,
	DeleteShift,
	publishShiftsInRange,
	PostShifts,
	Shift,
} from 'core/API/shifts';
import {
	getGeneralSettings,
	getJobCodes,
	getDepartments,
	getPositions,
	getSchedulerMetrics,
} from 'core/API/scheduler';
import {
	calculateCplh,
	calculateSplh,
	collectShiftMetricDates,
	formatMetricDate,
	getMetricDay,
	syncSchedulerMetricsForDates,
} from '../utils/schedulerMetricsUtils';
import {
	getShiftRequests,
	deleteShiftRequest,
	getShiftRequestTypes,
	approveOrDenyShiftRequest,
} from 'core/API/shift-requests';
import { deleteUserTimeOff } from 'core/API/userTimeOffs';
import { getUserJobCodes, groupJobCodeIdsByUserId } from 'core/API/userJobCodes';
import { ShiftRequest } from 'types/shift-requests';
import { cacheShiftRequestTypes } from 'core/utils/shiftRequestTypesCache';
import { IUsers } from 'core/interfaces/userInterface';
import { getEnrichedWorkplaceUsers, assignmentWorkplaceUserOptions } from 'core/utils/enrichWorkplaceUsers';
import { buildJobCodeIri, getEligibleJobCodeIds, isEmployeeScheduleable } from '../utils/jobCodeEligibility';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useIsMount } from 'core/hooks/useIsMounted';
import useModal from 'core/hooks/useModal';
import useSearchQuery from 'core/hooks/useQuery';
import { useAuth } from 'module/auth/context/AuthContext';
import { useDocumentContext } from 'module/documents/context';
import AddShiftPage, { IExtendedCellClick } from './AddShiftPage/AddShiftPage';
import EditStatsPage from './EditStatsPage/EditStatsPage';
import CopySchedulePage from './CopySchedulePage/CopySchedulePage';
// import HolidaysList from '../components/HolidaysList'; // Now using marquee in Events section
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';

import { CalendarType, TimeView } from '../interfaces/ScheduleInterface';
import { IShiftPayload } from '../interfaces/ShiftsInterface';
import DesktopPage from './DesktopPage/DesktopPageSchedule';
import FullCalendarSchedule from './DesktopPage/FullCalendarSchedule';
import PublishShiftsModal from './PublishShiftsModal';
import NoShiftsToPublishDialog from './NoShiftsToPublishDialog';
import ConfirmDialog from '../Modals/ConfirmDialog/ConfirmDialog';
import EmployeeRequestModal from '../components/EmployeeRequestModal/EmployeeRequestModal';
import RequestDetailView from '../components/RequestDetailView/RequestDetailView';
import ScheduleEventDetailDialog from '../components/ScheduleEventDetailDialog/ScheduleEventDetailDialog';
import TimeOffDetailDialog from '../components/TimeOffDetailDialog/TimeOffDetailDialog';
import ScheduleStatsBox from '../components/ScheduleStatsBox/ScheduleStatsBox';
import ShiftSchedulerDialog, {
	ShiftCreateContext,
	ShiftSchedulerDialogMode,
} from '../components/ShiftSchedulerDialog/ShiftSchedulerDialog';
import DepartmentFilter from '../components/DepartmentFilter/DepartmentFilter';
import { SCHEDULER_POSITIONS_QUERY_KEY } from '../utils/scheduleDepartmentUtils';
import {
	getPersistedDepartmentFilter,
	persistDepartmentFilter,
	resolveDepartmentFilterSelection,
} from '../utils/scheduleDepartmentFilterStorage';
import { useFullscreenContainer } from 'core/utils/fullscreenContainer';
import {
	buildDayViewResources,
	buildWeekViewResources,
	DepartmentFilterSelection,
	filterShiftsByDepartments,
	isDepartmentHeaderResourceId,
	findEmployeeResourceIds,
	parseWeekViewEmployeeId,
	resolveShiftResourceId,
} from '../utils/scheduleResourceUtils';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import DataTable from 'core/components/DataTable';
import { Edit, Delete } from '@mui/icons-material';

// @ts-ignore - SCSS module declaration is in react-app-env.d.ts
import './SchedulePage.style.scss';
import Grid from '@mui/material/Grid';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button as MuiButton,
	Typography as MuiTypography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useCalendarView } from 'core/hooks/useCalendarView';
import { getScheduleDateRange } from './SchedulePage.utils';
import {
	resolveStartOfWeekLabel,
} from '../utils/scheduleNavigation';
import {
	enrichShiftsWithEmployeeStatus,
	type EmployeeStatusData,
} from '../utils/scheduleEmployeeStatus';
import { getShiftLengthValidationError, getDefaultShiftDurationHours } from '../utils/shiftValidation';
import {
	buildShiftsMap,
	getShiftDates as formatRequestScheduleDates,
	lookupShift,
} from '../utils/shiftRequestScheduleDates';
import { isTimedTimeOff, resolveTimeOffRange } from '../utils/timeOffRequestForm';
import { buildAllWeekDays } from '../utils/availabilityDisplay';
import {
	canDeleteCalendarTimeOff,
	canViewCalendarTimeOff,
	getLiveUserTimeOffId,
	getTimeOffDeleteDialogCopy,
	getTimeOffRequestFromEvent,
	getUserTimeOffIdFromEvent,
	isCalendarTimeOffOwner,
	removeUserTimeOffFromUsers,
	shouldDisplayCalendarTimeOff,
} from '../utils/timeOffCalendarActions';

import momentTZ from 'moment-timezone';

momentTZ.tz.add(
	'America/Detroit|LMT CST EST EWT EPT EDT|5w.b 60 50 40 40 40|0123425252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252525252|-2Cgir.N peqr.N 156L0 8x40 iv0 6fd0 11z0 JxX1 SMX 1cN0 1cL0 aW10 1cL0 s10 1Vz0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|37e5'
);

export interface ICellClick {
	start_at: Date;
	end_at?: Date;
	resource: number;
	department?: number;
	roles?: number;
}

// Helper function to check if a date is in a past week
// Returns true only if the date is before the start of the current business week
const isDateInPastWeek = (
	date: Date | string,
	businessWeekStart: Date | string,
	startOfWeek: string
): boolean => {
	const inputDate = moment(date).startOf('day');

	// Calculate the current business week start
	const dayMap: { [key: string]: number } = {
		Sunday: 0,
		Monday: 1,
		Tuesday: 2,
		Wednesday: 3,
		Thursday: 4,
		Friday: 5,
		Saturday: 6,
	};

	const startDayOfWeek = dayMap[startOfWeek] ?? 1;
	const today = moment();
	const todayDayOfWeek = today.day();

	let daysBack = todayDayOfWeek - startDayOfWeek;
	if (daysBack < 0) {
		daysBack += 7;
	}

	const currentWeekStart = today.clone().subtract(daysBack, 'days').startOf('day');

	// Date is in the past only if it's before the current business week start
	return inputDate.isBefore(currentWeekStart);
};

// Toggle between Mobiscroll and FullCalendar implementations
// Set to true to use new FullCalendar (with proper business week support)
// Set to false to use old Mobiscroll (for comparison/rollback)
const USE_FULLCALENDAR = true;

export default function SchedulePage(): JSX.Element {
	const _auth = useAuth();
	const _isMounted = useIsMount();
	const theme = useTheme();
	const isRequestDetailMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const scheduleUserId = _auth.user?.id ?? localStorage.getItem('userId') ?? undefined;

	// Permission checks
	const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } =
		usePermissionCheck();

	// Scheduler permission checks
	const canApplyForRequests = hasExecutePermission('Schedule - My');
	const canViewMySchedule = hasViewPermission('Schedule - My');

	// Scheduler all employees permission checks
	const canViewAllSchedules = hasViewPermission('Schedule - All');
	const canPersonalizeEmployeeRowOrder = canViewMySchedule || canViewAllSchedules;
	const canEditShifts = hasEditPermission('Schedule - All');
	const canDeleteShiftsAll = hasDeletePermission('Schedule - All');
	// const canDeleteShiftsMy = hasDeletePermission('Schedule - My');
	const canDeleteShifts = canDeleteShiftsAll;
	const canCreateShifts = hasEditPermission('Schedule - All');

	const canManageAllRequests = hasExecutePermission('Schedule - All');

	const canManageShifts = canEditShifts || canDeleteShifts || canCreateShifts;
	const canSubmitRequests = canApplyForRequests;

	// Check if user can publish (Scheduler all employees Execute permission)
	const canPublishShifts = hasExecutePermission('Schedule - All');

	const queryClient = useQueryClient();

	const { push } = useHistory();

	const { workplaceId, timezones, workplace, data: workplacesData } = useWorkplacesContext();

	const { showToast } = useToastContext();
	const fullscreenContainer = useFullscreenContainer();

	// Get businessWeekStart from document context
	const { businessWeekStart, setScheduleView } = useDocumentContext();

	const [editEvent, setEditEvent] = useState(null);
	const [addNewEvent, setAddNewEvent] = useState<IExtendedCellClick>();
	const [shiftSchedulerDialog, setShiftSchedulerDialog] = useState<{
		open: boolean;
		mode: ShiftSchedulerDialogMode;
		shift?: any;
		createContext?: ShiftCreateContext;
	}>({
		open: false,
		mode: 'quick-edit-day',
	});
	const [currentView, setCurrentView] = useState<
		| 'scheduler'
		| 'add-shift'
		| 'edit-shift'
		| 'employee-request'
		| 'edit-stats'
		| 'copy-schedule'
	>('scheduler');

	// Sync currentView with context so HeaderBar can access it
	useEffect(() => {
		setScheduleView(currentView);
	}, [currentView, setScheduleView]);

	// Full-page edit/create views replace the calendar; reset layout scroll so Back/Save stay visible
	useEffect(() => {
		if (currentView === 'scheduler') return;

		window.scrollTo(0, 0);
		document.querySelector('main')?.scrollTo(0, 0);
	}, [currentView]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [shiftToDelete, setShiftToDelete] = useState<any>(null);
	const shiftMetricDatesRef = useRef<string[]>([]);
	const [openPublishModal, setOpenPublishModal] = useState(false);
	const [openNoShiftsPublishDialog, setOpenNoShiftsPublishDialog] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState<any>(null);
	const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
	const [editRequest, setEditRequest] = useState<ShiftRequest | null>(null);
	const [deleteRequestConfirmOpen, setDeleteRequestConfirmOpen] = useState(false);
	const [requestToDelete, setRequestToDelete] = useState<number | null>(null);
	const [selectedRequestForDetail, setSelectedRequestForDetail] = useState<ShiftRequest | null>(
		null
	);
	const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);
	const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState<ShiftRequest | null>(null);
	const [isDeleteTimeOffDialogOpen, setIsDeleteTimeOffDialogOpen] = useState(false);
	const [timeOffToDelete, setTimeOffToDelete] = useState<any>(null);
	const [removedUserTimeOffIds, setRemovedUserTimeOffIds] = useState<Set<number>>(
		() => new Set()
	);

	// ************************ USE MODAL HOOKS ************************
	const {
		open: _isAddShiftModalOpen,
		openModal: _openAddShiftModal,
		closeModal: _closeAddShiftModal,
	} = useModal();

	const {
		open: _isEditShiftModalOpen,
		openModal: _openEditShiftModal,
		closeModal: _closeEditShiftModal,
	} = useModal();

	const {
		open: _isAddEventModalOpen,
		openModal: _openAddEventModal,
		closeModal: _closeAddEventModal,
	} = useModal();

	const {
		open: _isEditEventModalOpen,
		openModal: _openEditEventModal,
		closeModal: _closeEditEventModal,
	} = useModal();

	const {
		open: _isRequestAvailableModalOpen,
		openModal: _openRequestAvailableModalOpen,
		closeModal: _closeRequestAvailableModalOpen,
	} = useModal();

	const {
		open: _isTradeRequestModalOpen,
		openModal: _openTradeRequestModal,
		closeModal: _closeTradeRequestModal,
	} = useModal();

	const {
		open: _isTimeOffRequestModalOpen,
		openModal: _openTimeOffRequestModal,
		closeModal: _closeTimeOffRequestModal,
	} = useModal();

	const {
		open: _isShiftPickModalOpen,
		openModal: _openShiftPickModal,
		closeModal: _closeShiftPickModal,
	} = useModal();

	const {
		open: _isCoverRequestModalOpen,
		openModal: _openCoverRequestModal,
		closeModal: _closeCoverRequestModal,
	} = useModal();

	const {
		open: _isCustomerDatabaseModalOpen,
		openModal: _openCustomerDatabaseModal,
		closeModal: _closeCustomerDatabaseModal,
	} = useModal();

	// copy schedule modal
	const {
		open: _isCopyScheduleOpen,
		openModal: _openCopySchedule,
		closeModal: _closeCopySchedule,
	} = useModal();

	const [_copyFromDate, _setCopyFromDate] = useState('');
	const [_copyToDate, _setCopyToDate] = useState('');

	// Fetch organization settings to get start_of_day
	const { data: organizationData } = useQuery('organizations', () => getOrganizations(), {
		staleTime: 300000,
	});

	// Fetch general settings for scheduler (minor age, overtime settings)
	const { data: generalSettings } = useQuery('generalSettings', () => getGeneralSettings(), {
		staleTime: 300000,
	});

	// Fetch all job codes for day view resources
	const { data: jobCodesData } = useQuery('jobCodes', () => getJobCodes(), {
		staleTime: 300000,
	});

	const { data: departmentsData } = useQuery('schedulerDepartments', () => getDepartments(), {
		staleTime: 300000,
		retry: false,
	});

	const { data: positionsData, refetch: refetchPositions } = useQuery(
		SCHEDULER_POSITIONS_QUERY_KEY,
		() => getPositions(),
		{
			staleTime: 0,
			refetchOnMount: true,
			retry: false,
		}
	);

	const departments = departmentsData || [];
	const positions = positionsData || [];

	useEffect(() => {
		if (
			shiftSchedulerDialog.open ||
			currentView === 'edit-shift' ||
			currentView === 'add-shift'
		) {
			refetchPositions();
		}
	}, [shiftSchedulerDialog.open, currentView, refetchPositions]);

	const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<DepartmentFilterSelection>('all');

	useEffect(() => {
		if (departments.length === 0) {
			setSelectedDepartmentIds('all');
			return;
		}

		const stored = workplaceId
			? getPersistedDepartmentFilter(workplaceId, scheduleUserId)
			: null;
		setSelectedDepartmentIds(resolveDepartmentFilterSelection(stored, departments));
	}, [departmentsData, workplaceId, scheduleUserId]);

	const handleDepartmentFilterChange = useCallback(
		(selection: DepartmentFilterSelection) => {
			setSelectedDepartmentIds(selection);
			if (workplaceId) {
				persistDepartmentFilter(workplaceId, selection, departments, scheduleUserId);
			}
		},
		[workplaceId, departments, scheduleUserId]
	);

	// Fetch days data for availability checking
	const { data: daysData } = useQuery(['days'], () => getDays(), {
		staleTime: 300000,
	});

	const allDays = useMemo(() => (daysData ? buildAllWeekDays(daysData) : []), [daysData]);

	// Fetch workplace employees enriched with full /users data
	const { data: workplaceEmployeesRaw, isLoading: isLoadingWorkplaceEmployees, refetch: refetchWorkplaceEmployees } = useQuery(
		['workplace-employees', workplaceId],
		() => getEnrichedWorkplaceUsers(workplaceId, assignmentWorkplaceUserOptions),
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	const { data: userJobCodesRaw = [] } = useQuery(
		['user-job-codes', workplaceId],
		() => getUserJobCodes(),
		{
			staleTime: 300000,
			enabled: !!workplaceId,
		}
	);

	const jobCodeIdsByUserId = useMemo(
		() => groupJobCodeIdsByUserId(userJobCodesRaw),
		[userJobCodesRaw]
	);

	// Transform and sort workplace employees by lastName ascending
	const workplaceUsers = useMemo(() => {
		if (!workplaceEmployeesRaw) return [];

		const usersArray = Array.isArray(workplaceEmployeesRaw)
			? workplaceEmployeesRaw
			: Object.values(workplaceEmployeesRaw as Record<string, IUsers>);
		const minorAge = generalSettings?.ageOfMinor || 18;

		return usersArray
			.map((user: IUsers) => {
				let isMinor = false;
				if (user.dateOfBirth) {
					const age = moment().diff(moment(user.dateOfBirth), 'years');
					isMinor = age < minorAge;
				}

				const userId = user.id;
				const fromJobCodesApi =
					userId != null ? jobCodeIdsByUserId.get(Number(userId)) || [] : [];
				// Merge API links with any eligibility already on the user so week view
				// keeps blank rows for every scheduleable employee.
				const eligibleIds = Array.from(
					new Set([...fromJobCodesApi, ...getEligibleJobCodeIds(user as any)])
				);
				return {
					'@id': userId != null ? `/api/users/${userId}` : user['@id'] || '',
					id: userId,
					firstName: user.firstName?.trim() || '',
					lastName: user.lastName?.trim() || '',
					email: user.email || '',
					dateOfBirth: user.dateOfBirth || null,
					userAvailability: user.userAvailability || [],
					userTimeOff: user.userTimeOff || [],
					isMinor: isMinor,
					eligibleJobCodes: eligibleIds.map((id) => buildJobCodeIri(id)),
				};
			})
			.filter((user) => Boolean(user['@id']))
			.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
	}, [workplaceEmployeesRaw, generalSettings, jobCodeIdsByUserId]);

	const isWorkplaceUsersReady =
		!!workplaceId &&
		!isLoadingWorkplaceEmployees &&
		workplaceEmployeesRaw !== undefined;

	// Get start_of_day from organization (default to '00:00' if not found)
	const startOfDay = useMemo(() => {
		return organizationData?.data?.[0]?.start_of_day || '00:00';
	}, [organizationData]);

	// Get start_of_week from organization (default to 'Monday' if not found)
	const startOfWeek = useMemo(
		() => resolveStartOfWeekLabel(organizationData?.data?.[0], daysData),
		[organizationData, daysData]
	);

	// ************************************************************************

	// url query string determin what viwe
	const location = useSearchQuery();

	const isValidParam = (paramName: string) => {
		const paramValue = location.get(paramName);
		let isValid = true;

		if (paramValue === 'undefined') {
			isValid = false;
		}
		if (paramValue === 'null') {
			isValid = false;
		}
		if (!paramValue) {
			isValid = false;
		}
		return isValid ? location.get(paramName) : paramName === 'time' ? 'day' : 'schedule';
	};

	// ************************************************************************
	const [timeView, _setView] = useState<TimeView>(isValidParam('time') as TimeView);
	// for event i have to leave this date state
	const [fromDate, setFromDate] = useState(() => {
		const [formatedFirstDay] = getScheduleDateRange(timeView, startOfWeek, daysData);
		return formatedFirstDay;
	});

	const [toDate, setToDate] = useState(() => {
		const [, formatedLastDay] = getScheduleDateRange(timeView, startOfWeek, daysData);
		return formatedLastDay;
	});

	// Initialize date range from org settings only before a business week is selected in HeaderBar
	useEffect(() => {
		if (businessWeekStart) return;

		const [formatedFirstDay, formatedLastDay] = getScheduleDateRange(
			timeView,
			startOfWeek,
			daysData
		);
		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);
	}, [startOfWeek, timeView, daysData, businessWeekStart]);

	const selectedWeekStartDate = useMemo(() => {
		if (businessWeekStart) {
			return moment(businessWeekStart).format('YYYY-MM-DD') + ' 00:00:00';
		}
		return fromDate;
	}, [businessWeekStart, fromDate]);

	const [_toPublish, _setToPublish] = useState(0);
	const [_conflicts, _setConflicts] = useState<Array<any>>([]);
	const [_isPublishable, setIsPublishable] = useState<boolean>(false);

	const [_allSchedules, setAllSchedules] = useState<MbscCalendarEvent[]>([]);
	const [_schedulesBackup, _setSchedulesBackup] = useState<MbscCalendarEvent[]>([]);
	const [_schedulesUnassigned, _setSchedulesUnassigned] = useState<any>([]);
	const [allEvents, setAllEvents] = useState<MbscCalendarEvent[]>([]);
	const [rawHolidays, setRawHolidays] = useState<any[]>([]); // Store raw holiday data
	// CalendarShift extends Shift with calendar-specific properties
	type CalendarShift = Shift & {
		start: Date;
		end: Date;
		shift_id: number;
		editable: boolean;
		role?: string;
		background_color?: string;
		text_color?: string;
		color?: string;
		resource?: string | number;
		title?: string;
		employeeStatusData?: EmployeeStatusData | null;
		employeeTimeOff?: any[]; // Employee's time-off data
		employeeAvailability?: any[]; // Employee's availability data
	};
	const [allShifts, setAllShifts] = useState<CalendarShift[]>([]);

	const filteredShifts = useMemo(() => {
		if (!jobCodesData) return allShifts;
		return filterShiftsByDepartments(allShifts, jobCodesData, selectedDepartmentIds);
	}, [allShifts, jobCodesData, selectedDepartmentIds]);

	// Track the last focused date to maintain focus when switching views
	const [lastFocusedDate, setLastFocusedDate] = useState<Date | null>(null);

	const [_requestData, setRequestData] = useState(null);

	const [calendarType, setCalendarType] = useState<CalendarType>(
		isValidParam('view') as CalendarType
	);

	const _usersData = queryClient.getQueryData<IUsers[]>(['users', workplaceId]);

	// Calculate workplace working hours start time for the business week start day
	const workplaceWorkingHoursStart = useMemo(() => {
		// Use workplace from context which has complete data including workplaceWorkingHours
		if (!workplace || !workplace.workplaceWorkingHours) return startOfDay;

		const workingHours = workplace.workplaceWorkingHours;

		if (!workingHours || workingHours.length === 0) return startOfDay;

		// If we don't have daysData or startOfWeek, fall back to earliest time across all days
		if (!daysData || !startOfWeek) {
			const earliestTime = workingHours.reduce((earliest: string, wh: any) => {
				const openTime = wh.openAt || '00:00:00';
				if (!earliest || openTime < earliest) {
					return openTime;
				}
				return earliest;
			}, '');
			return earliestTime ? earliestTime.substring(0, 5) : startOfDay;
		}

		// Create a map of days by their @id or id for quick lookup
		const daysMap = new Map();
		if (Array.isArray(daysData)) {
			daysData.forEach((day: any) => {
				const dayId = day['@id'] || `/api/days/${day.id}`;
				daysMap.set(dayId, day);
			});
		}

		// Determine the target day: if businessWeekStart is in the current week, use current day, otherwise use startOfWeek
		let targetDay = startOfWeek;
		if (businessWeekStart) {
			const currentWeekStart = moment(businessWeekStart);
			const currentWeekEnd = moment(businessWeekStart).add('7', 'days');
			const businessWeekMoment = moment();
			if (businessWeekMoment.isBetween(currentWeekStart, currentWeekEnd, null, '[]')) {
				// businessWeekStart is in the current week, use current day
				targetDay = moment().format('dddd'); // e.g., "Monday", "Tuesday", etc.
			}
		}

		// Find the working hours entry that matches the target day
		const weekStartWorkingHours = workingHours.find((wh: any) => {
			const dayReference = wh.day;
			const dayData = daysMap.get(dayReference);
			return dayData && dayData?.label === targetDay;
		});

		// If we found working hours for the week start day, use its openAt time
		if (weekStartWorkingHours && weekStartWorkingHours.openAt) {
			return weekStartWorkingHours.openAt.substring(0, 5);
		}

		// Fallback to earliest time if we couldn't find the specific day
		const earliestTime = workingHours.reduce((earliest: string, wh: any) => {
			const openTime = wh.openAt || '00:00:00';
			if (!earliest || openTime < earliest) {
				return openTime;
			}
			return earliest;
		}, '');

		// Return time in HH:mm format
		return earliestTime ? earliestTime.substring(0, 5) : startOfDay;
	}, [workplace, startOfDay, daysData, startOfWeek, businessWeekStart]);

	// Helper function to get working hours start for a specific date
	const getWorkingHoursForDate = useCallback(
		(date: Date | string): string => {
			const workingHours = workplace?.workplaceWorkingHours;
			if (!workingHours || workingHours.length === 0) return startOfDay;
			if (!daysData) return startOfDay;

			// Create a map of days by their @id or id for quick lookup
			const daysMap = new Map();
			if (Array.isArray(daysData)) {
				daysData.forEach((day: any) => {
					const dayId = day['@id'] || `/api/days/${day.id}`;
					daysMap.set(dayId, day);
				});
			}

			// Get the day of the week for the given date (e.g., "Monday", "Tuesday")
			const targetDay = moment(date).format('dddd');

			// Find the working hours entry that matches the target day
			const dayWorkingHours = workingHours.find((wh: any) => {
				const dayReference = wh.day;
				const dayData = daysMap.get(dayReference);
				return dayData && dayData?.label === targetDay;
			});

			// If we found working hours for the day, use its openAt time
			if (dayWorkingHours && dayWorkingHours.openAt) {
				return dayWorkingHours.openAt.substring(0, 5);
			}

			// Fallback to earliest time if we couldn't find the specific day
			const earliestTime = workingHours.reduce((earliest: string, wh: any) => {
				const openTime = wh.openAt || '00:00:00';
				if (!earliest || openTime < earliest) {
					return openTime;
				}
				return earliest;
			}, '');

			return earliestTime ? earliestTime.substring(0, 5) : startOfDay;
		},
		[workplace, daysData, startOfDay]
	);

	const [filters, setFilters] = useState([
		{ id: 3, name: 'Pick', active: true },
		{ id: 4, name: 'Availability', active: true },
		{ id: 2, name: 'Time off', active: true },
		{ id: 5, name: 'Cover', active: true },
		{ id: 6, name: 'Trade', active: true },
	]);

	const setCalendarTypeHandler = (view: CalendarType) => {
		push(`?view=${view}&time=${timeView}`);
		setCalendarType(view);
	};

	// useCalendarView to decide whether we're in 'day' or 'week' view
	const { calendarView } = useCalendarView();

	// Filter shifts for day view - now shows all shifts in the week (same as week view)
	// Day view and week view both show the entire business week, just with different granularity

	// Create workplace users map by @id for efficient lookup
	const workplaceUsersMap = useMemo(() => {
		if (!workplaceUsers || !Array.isArray(workplaceUsers)) return new Map();
		return new Map(workplaceUsers.map((user) => [user['@id'], user]));
	}, [workplaceUsers]);
	// Generate resources based on calendar view
	const resourcesToPass = useMemo(() => {
		// Get shift requests from query client to avoid dependency issues
		const cachedShiftRequests = queryClient.getQueryData<any>([
			'shift-requests',
			workplaceId,
			canManageAllRequests,
		]);

		if (calendarView === 'day') {
			if (!jobCodesData || jobCodesData.length === 0) return undefined;

			if (departments.length > 0) {
				return buildDayViewResources({
					jobCodes: jobCodesData,
					departments,
					selectedDepartmentIds,
				});
			}

			const jobCodeResources = jobCodesData.map((jobCode: any, index: number) => ({
				id: jobCode.id,
				name: jobCode.jobNumber
					? `${String(jobCode.jobNumber).padStart(2, '0')} ${jobCode.label}`
					: jobCode.label,
				background_color: jobCode.backgroundColor,
				text_color: jobCode.textColor,
				jobCode: jobCode,
				sortOrder: index,
			}));

			return [
				{
					id: 'timeoff',
					name: 'Time Off',
					displayName: 'Time Off',
					sortOrder: -1,
				},
				...jobCodeResources,
			];
		}

		if (calendarView === 'week' || calendarView === 'workweek') {
			const weekStart = moment(businessWeekStart || fromDate).startOf('day');
			const weekEnd = businessWeekStart
				? moment(businessWeekStart).add(6, 'days').endOf('day')
				: moment(toDate).endOf('day');

			if (departments.length > 0) {
				return buildWeekViewResources({
					allShifts: filteredShifts,
					jobCodes: jobCodesData || [],
					departments,
					selectedDepartmentIds,
					weekStart,
					weekEnd,
					employees: workplaceUsers,
				});
			}

			// Week view: Show scheduleable employees as resources, including those with no shifts
			const employeeResourcesMap = new Map();

			workplaceUsers.forEach((user) => {
				if (user?.id == null) return;
				if (!isEmployeeScheduleable(user)) return;
				employeeResourcesMap.set(String(user.id), {
					id: user.id,
					name: `${user.firstName} ${user.lastName}`,
					employee: user,
					totalHours: 0,
					shiftCount: 0,
					userAvailability: user.userAvailability || [],
				});
			});

			// Group shifts by employee and calculate total hours for the week
			filteredShifts.forEach((shift) => {
				const shiftStart = moment(shift.start);
				const shiftEnd = moment(shift.end);

				// Only include shifts within the business week (7 days from start_of_week)
				if (shiftStart.isBetween(weekStart, weekEnd, null, '[]')) {
					const employee = shift.employee;
					if (employee && employee.id) {
						const employeeKey = String(employee.id);
						const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
						const shiftHours = shiftDuration.asHours();

						if (!employeeResourcesMap.has(employeeKey)) {
							employeeResourcesMap.set(employeeKey, {
								id: employee.id,
								name: `${employee.firstName} ${employee.lastName}`,
								employee: employee,
								totalHours: shiftHours,
								shiftCount: 1,
								userAvailability: employee.userAvailability || [],
							});
						} else {
							const existing = employeeResourcesMap.get(employeeKey);
							existing.totalHours += shiftHours;
							existing.shiftCount += 1;
						}
					} else {
						// Handle house shifts (shifts without assigned employees)
						const openShiftKey = 'open-shifts';
						const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
						const shiftHours = shiftDuration.asHours();

						if (!employeeResourcesMap.has(openShiftKey)) {
							employeeResourcesMap.set(openShiftKey, {
								id: openShiftKey,
								name: 'House Shifts',
								employee: null,
								totalHours: shiftHours,
								shiftCount: 1,
								isOpenShift: true,
							});
						} else {
							const existing = employeeResourcesMap.get(openShiftKey);
							existing.totalHours += shiftHours;
							existing.shiftCount += 1;
						}
					}
				}
			});

			// Convert map to array and add display names with hours, overtime, and minor indicators
			const employeeResources = Array.from(employeeResourcesMap.values()).map((resource) => {
				// Weekly overtime thresholds
				const weeklyOTThreshold = generalSettings?.overtimeWeeklyHoursAt ?? 40;
				const weeklyOTWarning = generalSettings?.overtimeWeeklyWarningAt ?? weeklyOTThreshold;
				const hasWeeklyOT = resource.totalHours > weeklyOTThreshold;
				const isApproachingWeeklyOT = !hasWeeklyOT && resource.totalHours >= weeklyOTWarning;
				const weeklyOTHours = hasWeeklyOT ? resource.totalHours - weeklyOTThreshold : 0;

				// Daily overtime thresholds - check each shift for this employee
				const dailyOTThreshold = generalSettings?.overtimeDailyHoursAt ?? 8;
				const dailyOTWarning = generalSettings?.overtimeDailyWarningAt ?? dailyOTThreshold * 0.9;

				// Find shifts for this employee within the selected week and check for daily overtime
				const employeeShifts = filteredShifts.filter((shift) => {
					if (shift.employee?.id !== resource.employee?.id) return false;
					const shiftStart = moment(shift.start);
					return shiftStart.isBetween(weekStart, weekEnd, null, '[]');
				});

				let hasDailyOT = false;
				let isApproachingDailyOT = false;
				let maxDailyHours = 0;

				// Group shifts by day and calculate hours per day
				const shiftsByDay = new Map<string, number>();
				employeeShifts.forEach((shift) => {
					const shiftStart = moment(shift.start);
					const shiftEnd = moment(shift.end);
					const dayKey = shiftStart.format('YYYY-MM-DD');

					const shiftDuration = moment.duration(shiftEnd.diff(shiftStart));
					const shiftHours = shiftDuration.asHours();

					const currentDayHours = shiftsByDay.get(dayKey) || 0;
					const newDayHours = currentDayHours + shiftHours;
					shiftsByDay.set(dayKey, newDayHours);

					if (newDayHours > maxDailyHours) {
						maxDailyHours = newDayHours;
					}

					if (newDayHours > dailyOTThreshold) {
						hasDailyOT = true;
					} else if (newDayHours >= dailyOTWarning) {
						isApproachingDailyOT = true;
					}
				});

				// Determine overall overtime status (daily takes precedence over weekly)
				// const hasOT = hasDailyOT || hasWeeklyOT;

				// Calculate total overtime hours for display
				const dailyOTHours = hasDailyOT ? maxDailyHours - dailyOTThreshold : 0;
				
				// Calculate total daily overtime across all days in the week
				let totalDailyOvertimeHours = 0;
				shiftsByDay.forEach((dayHours) => {
					if (dayHours > dailyOTThreshold) {
						totalDailyOvertimeHours += (dayHours - dailyOTThreshold);
					}
				});

				// Check for minor age using workplace users map
				const minorAge = generalSettings?.ageOfMinor || 18;
				const workplaceUser = workplaceUsersMap.get(resource.employee?.['@id']);
				const employeeDOB = workplaceUser?.dateOfBirth;
				const isMinor = employeeDOB
					? moment().diff(moment(employeeDOB), 'years') < minorAge
					: false;

				// Check for availability and time-off conflicts using user data
				const employeeRequests =
					cachedShiftRequests?.member?.filter(
						(req: any) => req.employeeRequest?.id === resource.employee?.id
					) || [];

				// Check if employee has time off from userTimeOff field
				const hasTimeOff = (workplaceUser?.userTimeOff || []).some((timeOff: any) => {
					if (timeOff.startAt && timeOff.endAt) {
						const timeOffStart = moment(`${timeOff.startAt}.000Z`);
						const timeOffEnd = moment(`${timeOff.endAt}.000Z`);
						return (
							timeOffStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
							timeOffEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
							(timeOffStart.isSameOrBefore(weekStart) && timeOffEnd.isSameOrAfter(weekEnd))
						);
					}
					return false;
				});

				// Check if employee has availability issues from userAvailability field
				const hasAvailabilityIssue = (workplaceUser?.userAvailability || []).some((avail: any) => {
					if (avail.startAt && avail.endAt) {
						const availStart = moment(`${avail.startAt}.000Z`);
						const availEnd = moment(`${avail.endAt}.000Z`);
						return (
							availStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
							availEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
							(availStart.isSameOrBefore(weekStart) && availEnd.isSameOrAfter(weekEnd))
						);
					}
					return false;
				});

				// Get availability data for the current week
				const userAvailabilityThisWeek = (workplaceUser?.userAvailability || []).filter(
					(avail: any) => {
						if (avail.startAt && avail.endAt) {
							const availStart = moment(`${avail.startAt}.000Z`);
							const availEnd = moment(`${avail.endAt}.000Z`);
							return (
								availStart.isBetween(weekStart, weekEnd, 'day', '[]') ||
								availEnd.isBetween(weekStart, weekEnd, 'day', '[]') ||
								(availStart.isSameOrBefore(weekStart) && availEnd.isSameOrAfter(weekEnd))
							);
						}
						return false;
					}
				);
				// Build display name with indicators
				const statusParts = [];

				if (hasDailyOT) {
					statusParts.push(`Daily OT: ${dailyOTHours.toFixed(1)}h`);
				}

				if (hasWeeklyOT) {
					statusParts.push(`Weekly OT: ${weeklyOTHours.toFixed(1)}h`);
				}

				if (isMinor) {
					statusParts.push('Minor');
				}
				const displaySuffix =
					statusParts.length > 0
						? ` (${resource.totalHours.toFixed(1)}h, ${statusParts.join(', ')})`
						: ` (${resource.totalHours.toFixed(1)}h)`;

				return {
					...resource,
					name: resource.isOpenShift
						? `${resource.name} (${resource.totalHours.toFixed(1)}h)`
						: `${resource.name}${displaySuffix}`,
					displayName: resource.name, // Keep original name for display
					hasOvertime: hasDailyOT || hasWeeklyOT,
					isApproachingDailyOvertime: isApproachingDailyOT && !hasDailyOT,
					isApproachingWeeklyOvertime: isApproachingWeeklyOT && !hasWeeklyOT,
					overtimeHours: dailyOTHours,
					weeklyOvertimeHours: weeklyOTHours,
					totalDailyOvertimeHours: totalDailyOvertimeHours,
					hasDailyOvertime: hasDailyOT,
					hasWeeklyOvertime: hasWeeklyOT,
					maxDailyHours: maxDailyHours,
					dailyOTWarning: dailyOTWarning,
					weeklyOTWarning: weeklyOTWarning,
					isMinor: isMinor,
					hasTimeOff: hasTimeOff,
					hasAvailabilityIssue: hasAvailabilityIssue,
					userAvailability:
						workplaceUser?.userAvailability ||
						resource.employee?.userAvailability ||
						userAvailabilityThisWeek ||
						[],
					employeeRequests: employeeRequests,
				};
			}); // Sort by last name (house shifts last when not grouped by department)
			employeeResources.sort((a, b) => {
				if (a.isOpenShift && !b.isOpenShift) return 1;
				if (!a.isOpenShift && b.isOpenShift) return -1;
				const aLastName = a.employee?.lastName || a.displayName || a.name;
				const bLastName = b.employee?.lastName || b.displayName || b.name;
				const lastNameCompare = aLastName.localeCompare(bLastName);
				if (lastNameCompare !== 0) return lastNameCompare;
				const aFirstName = a.employee?.firstName || '';
				const bFirstName = b.employee?.firstName || '';
				return aFirstName.localeCompare(bFirstName);
			});

			return employeeResources;
		}

		return undefined;
	}, [
		calendarView,
		filteredShifts,
		generalSettings,
		fromDate,
		toDate,
		businessWeekStart,
		jobCodesData,
		departments,
		selectedDepartmentIds,
		queryClient,
		workplaceId,
		canManageAllRequests,
		workplaceUsersMap,
		workplaceUsers,
	]);

	// Determine the selected date for the calendar (passed to programmatic scrolling)
	// Use businessWeekStart as the anchor, with lastFocusedDate for scroll position
	const calendarSelectedDate = useMemo(() => {
		if (!businessWeekStart) return undefined;

		// Parse the working hours start time
		const workingHoursTime = workplaceWorkingHoursStart || startOfDay || '00:00';
		const [hours, minutes] = workingHoursTime.split(':').map(Number);

		const weekStartDate = moment(businessWeekStart).startOf('day');
		const weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');

		// Start with business week start as the base
		let targetDate = weekStartDate.clone();

		// Priority 1: Use lastFocusedDate if within business week (user interaction took precedence)
		if (lastFocusedDate) {
			const focusedDateMoment = moment(lastFocusedDate);

			if (focusedDateMoment.isBetween(weekStartDate, weekEndDate, null, '[]')) {
				targetDate = focusedDateMoment.clone();
			}
			return targetDate.toDate();
		} else {
			// Priority 2: For current week with no lastFocusedDate, scroll to today
			const today = moment().startOf('day');
			const isCurrentWeek = today.isBetween(weekStartDate, weekEndDate, null, '[]');

			if (isCurrentWeek) {
				targetDate = today.clone();
			}
			// Priority 3: For other weeks, use business week start (already set)
		}

		// Set the time for scroll position
		targetDate.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

		return targetDate.toDate();
	}, [businessWeekStart, workplaceWorkingHoursStart, startOfDay, lastFocusedDate]);

	const metricsWeekRange = useMemo(() => {
		if (businessWeekStart) {
			return {
				start: moment(businessWeekStart).format('YYYY-MM-DD'),
				end: moment(businessWeekStart).add(6, 'days').format('YYYY-MM-DD'),
			};
		}

		if (fromDate && toDate) {
			return {
				start: moment(fromDate).format('YYYY-MM-DD'),
				end: moment(toDate).format('YYYY-MM-DD'),
			};
		}

		return null;
	}, [businessWeekStart, fromDate, toDate]);

	// Fetch scheduler metrics for the current business week
	const { data: allMetricsData, refetch: _refetchMetrics } = useQuery(
		['scheduler-metrics', workplaceId, metricsWeekRange?.start, metricsWeekRange?.end],
		() => {
			if (!workplaceId || !metricsWeekRange) return Promise.resolve([]);
			return getSchedulerMetrics(workplaceId, metricsWeekRange.start, metricsWeekRange.end);
		},
		{
			enabled: !!workplaceId && !!metricsWeekRange && canViewAllSchedules,
			// refetchOnWindowFocus: true,
		}
	);

	// Filter metrics data based on current business week range
	const weekMetricsData = useMemo(() => {
		if (!allMetricsData || !Array.isArray(allMetricsData)) return [];

		// Use businessWeekStart if available, otherwise fall back to fromDate/toDate
		let weekStart: string;
		let weekEnd: string;

		if (businessWeekStart) {
			// Use businessWeekStart for consistent date range with shift calculations
			weekStart = moment(businessWeekStart).format('YYYY-MM-DD');
			weekEnd = moment(businessWeekStart).add(6, 'days').format('YYYY-MM-DD');
		} else if (fromDate && toDate) {
			// Fallback to fromDate/toDate
			weekStart = moment(fromDate).format('YYYY-MM-DD');
			weekEnd = moment(toDate).format('YYYY-MM-DD');
		} else {
			return [];
		}

		return allMetricsData.filter((metric: any) => {
			const metricDay = getMetricDay(metric);
			return metricDay ? metricDay >= weekStart && metricDay <= weekEnd : false;
		});
	}, [allMetricsData, businessWeekStart, fromDate, toDate]);

	const metricsForDisplay = useMemo(() => {
		if (calendarView === 'day' && calendarSelectedDate) {
			const selectedDay = formatMetricDate(calendarSelectedDate);
			return weekMetricsData.filter((metric: any) => getMetricDay(metric) === selectedDay);
		}

		return weekMetricsData;
	}, [weekMetricsData, calendarView, calendarSelectedDate]);

	const syncSchedulerMetricsForShiftDates = useCallback(
		async (dates: Array<string | Date | null | undefined>) => {
			if (!workplaceId) return;

			const normalizedDates = collectShiftMetricDates(...dates);
			if (normalizedDates.length === 0) return;

			try {
				const freshData = await queryClient.fetchQuery(
					['shifts', workplaceId, fromDate, toDate],
					() =>
						fetchAllShifts({
							workplaceId,
							fromDate,
							toDate,
						})
				);

				await syncSchedulerMetricsForDates({
					workplaceId,
					dates: normalizedDates,
					shifts: freshData?.member || [],
					dailyOTThreshold: generalSettings?.overtimeDailyHoursAt ?? 8,
				});

				queryClient.invalidateQueries([
					'scheduler-metrics',
					workplaceId,
					metricsWeekRange?.start,
					metricsWeekRange?.end,
				]);
			} catch (error) {
				console.error('Error syncing scheduler metrics:', error);
			}
		},
		[
			workplaceId,
			fromDate,
			toDate,
			generalSettings,
			queryClient,
			metricsWeekRange?.start,
			metricsWeekRange?.end,
		]
	);

	// Calculate schedule statistics
	const scheduleStats = useMemo(() => {
		// Get business week boundaries based on businessWeekStart from context
		let weekStartDate: moment.Moment;
		let weekEndDate: moment.Moment;

		if (businessWeekStart) {
			// Use businessWeekStart from context
			weekStartDate = moment(businessWeekStart).startOf('day');
			weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
		} else {
			// Fallback: calculate from fromDate and startOfWeek
			const dayMap: { [key: string]: number } = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			const startDayOfWeek = dayMap[startOfWeek] ?? 1;
			const currentDate = moment(fromDate);
			const currentDayOfWeek = currentDate.day();

			let daysBack = currentDayOfWeek - startDayOfWeek;
			if (daysBack < 0) {
				daysBack += 7;
			}

			weekStartDate = currentDate.clone().subtract(daysBack, 'days').startOf('day');
			weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
		}

		// Filter shifts to only include those within the business week
		const weekShifts = allShifts.filter((shift) => {
			const shiftStart = moment(shift.start);
			return shiftStart.isBetween(weekStartDate, weekEndDate, null, '[]');
		});

		// Calculate total hours scheduled based on current view
		let totalHours = 0;

		if (calendarView === 'day') {
			// For day view, only count hours for the selected day
			const currentDay = moment(calendarSelectedDate).startOf('day');
			const currentDayEnd = moment(calendarSelectedDate).endOf('day');

			totalHours = weekShifts.reduce((total, shift) => {
				const shiftStart = moment(shift.start);
				// Only include shifts that occur on the selected day
				if (shiftStart.isBetween(currentDay, currentDayEnd, null, '[]')) {
					const start = moment(shift.start);
					const end = moment(shift.end);
					const hours = end.diff(start, 'hours', true);
					return total + hours;
				}
				return total;
			}, 0);
		} else {
			// For week and workweek views, count hours for the entire week
			totalHours = weekShifts.reduce((total, shift) => {
				const start = moment(shift.start);
				const end = moment(shift.end);
				const hours = end.diff(start, 'hours', true);
				return total + hours;
			}, 0);
		}

		// Calculate daily OT and weekly OT for the entire week
		const dailyOTHoursThreshold = generalSettings?.overtimeDailyHoursAt ?? 8;
		const weeklyOTHoursThreshold = generalSettings?.overtimeWeeklyHoursAt ?? 40;

		const employeeDailyHoursMap = new Map<string, Map<string, number>>(); // employeeId -> (date -> hours)
		const employeeWeeklyHoursMap = new Map<string, number>(); // employeeId -> total weekly hours

		weekShifts.forEach((shift) => {
			if (shift.employee) {
				const employeeId = shift.employee.id?.toString() || shift.employee.toString();
				const shiftStart = moment(shift.start);
				const shiftEnd = moment(shift.end);
				const shiftDate = shiftStart.format('YYYY-MM-DD');
				const shiftHours = shiftEnd.diff(shiftStart, 'hours', true);

				// Track daily hours per employee
				if (!employeeDailyHoursMap.has(employeeId)) {
					employeeDailyHoursMap.set(employeeId, new Map<string, number>());
				}
				const employeeDailyHours = employeeDailyHoursMap.get(employeeId)!;
				employeeDailyHours.set(shiftDate, (employeeDailyHours.get(shiftDate) || 0) + shiftHours);

				// Track total weekly hours per employee
				employeeWeeklyHoursMap.set(
					employeeId,
					(employeeWeeklyHoursMap.get(employeeId) || 0) + shiftHours
				);
			}
		});

		// Calculate daily OT as the weekly total of all daily OT during the week
		let dailyOT = 0;
		if (calendarView === 'day') {
			const selectedDay = moment(calendarSelectedDate).format('YYYY-MM-DD');
			employeeDailyHoursMap.forEach((dailyHours) => {
				const hoursWorked = dailyHours.get(selectedDay) || 0;
				if (hoursWorked > dailyOTHoursThreshold) {
					dailyOT += hoursWorked - dailyOTHoursThreshold;
				}
			});
		} else {
			employeeDailyHoursMap.forEach((dailyHours) => {
				dailyHours.forEach((hoursWorked) => {
					if (hoursWorked > dailyOTHoursThreshold) {
						dailyOT += hoursWorked - dailyOTHoursThreshold;
					}
				});
			});
		}

		// Calculate weekly OT as the weekly total of all OT that week (hours over weekly threshold)
		let weeklyOT = 0;
		employeeWeeklyHoursMap.forEach((weeklyHours) => {
			if (weeklyHours > weeklyOTHoursThreshold) {
				weeklyOT += weeklyHours - weeklyOTHoursThreshold;
			}
		});

		return {
			sales: 0, // Will be updated below
			splh: 0,
			cplh: 0,
			hoursScheduled: totalHours,
			dailyOT: dailyOT,
			weeklyOT: weeklyOT,
			customers: 0,
		};
	}, [
		allShifts,
		generalSettings,
		businessWeekStart,
		fromDate,
		startOfWeek,
		calendarView,
		calendarSelectedDate,
]);

	// Merge live shift hours with projected sales/customers from scheduler metrics
	const statsWithMetrics = useMemo(() => {
		const totalSales = metricsForDisplay.reduce(
			(total: number, metric: any) => total + (metric.sales || 0),
			0
		);
		const totalCustomers = metricsForDisplay.reduce(
			(total: number, metric: any) => total + (metric.customers || 0),
			0
		);
		const hoursScheduled = scheduleStats.hoursScheduled;

		return {
			...scheduleStats,
			sales: totalSales,
			customers: totalCustomers,
			splh: calculateSplh(totalSales, hoursScheduled),
			cplh: calculateCplh(totalCustomers, hoursScheduled),
		};
	}, [metricsForDisplay, scheduleStats]);
	const { mutate: _updateOneShift, isLoading: _loadingUpdateOneShift } = useMutation(PutOneShift);

	const { mutate: updateShiftOnDrag } = useMutation(
		(params: {
			id: number;
			body: IShiftPayload;
			originalStartDate?: string | null;
			originalEndDate?: string | null;
		}) => PutOneShift({ id: params.id, body: params.body }),
		{
			onSuccess: async (_data, variables) => {
				queryClient.invalidateQueries(['shifts']);
				await refetchShifts();

				await syncSchedulerMetricsForShiftDates([
					variables.body.startAt,
					variables.originalStartDate,
					variables.originalEndDate,
					variables.body.endAt,
				]);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift');
				refetchShifts(); // Refetch to revert UI to correct state
			},
		}
	);

	const { mutate: deleteShift, isLoading: isDeleteLoading } = useMutation(DeleteShift, {
		onSuccess: async () => {
			queryClient.invalidateQueries(['shifts']);
			await refetchShifts();
			await syncSchedulerMetricsForShiftDates(shiftMetricDatesRef.current);
			setIsDeleteDialogOpen(false);
			setShiftToDelete(null);
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to delete shift');
		},
	});

	const { mutate: copyShift } = useMutation(PostShifts, {
		onSuccess: async (_data, variables) => {
			queryClient.invalidateQueries(['shifts']);
			await refetchShifts();
			await syncSchedulerMetricsForShiftDates([variables.startAt]);
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to copy shift');
		},
	});

	const { mutate: createShiftFromDialog, isLoading: isCreateShiftLoading } = useMutation(PostShifts, {
		onSuccess: async (_data, variables) => {
			queryClient.invalidateQueries(['shifts']);
			await refetchShifts();
			await syncSchedulerMetricsForShiftDates([variables.startAt]);
			setShiftSchedulerDialog((prev) => ({ ...prev, open: false, createContext: undefined }));
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to add shift');
		},
	});

	const { mutate: updateShiftQuick, isLoading: isQuickEditSaving } = useMutation(
		(params: {
			id: number;
			body: IShiftPayload;
			keepOpen?: boolean;
			metricDates?: Array<string | Date | null | undefined>;
		}) => PutOneShift({ id: params.id, body: params.body }),
		{
			onSuccess: async (_data, variables) => {
				queryClient.invalidateQueries(['shifts']);
				await refetchShifts();
				await syncSchedulerMetricsForShiftDates(
					variables.metricDates || [variables.body.startAt]
				);
				if (!variables.keepOpen) {
					setShiftSchedulerDialog((prev) => ({ ...prev, open: false, shift: undefined }));
				}
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift');
			},
		}
	);

	const { mutate: publishShifts, isLoading: _isPublishLoading } = useMutation(
		publishShiftsInRange,
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['shifts']);
				refetchShifts();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to publish shifts');
			},
		}
	);

	// Fetch shift requests
	// If user has execute permission on 'Schedule - All', fetch all requests
	// Fetch all shift requests (filtering done on frontend based on permission)
	const {
		data: shiftRequestsData,
		refetch: refetchShiftRequests,
		isLoading: isLoadingRequests,
	} = useQuery(
		['shift-requests', workplaceId, canManageAllRequests],
		() => {
			const params: any = { workplace_id: workplaceId, limit: 100 };
			// API does not support employee_id filtering, so fetch all and filter on frontend
			return getShiftRequests(params);
		},
		{
			enabled: !!workplaceId,
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to fetch shift requests');
			},
		}
	);

	// Fetch shift request types and cache them (called once on SchedulePage load)
	useQuery(['shift-request-types'], getShiftRequestTypes, {
		onSuccess: (data) => {
			// Cache the types when successfully fetched
			if (data?.['member']) {
				cacheShiftRequestTypes(data['member']);
			}
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to fetch request types');
		},
		staleTime: Infinity,
		cacheTime: Infinity,
	});

	// Delete shift request mutation
	const { mutate: deleteShiftRequestMutation, isLoading: isDeletingRequest } = useMutation(
		deleteShiftRequest,
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['shift-requests']);
				refetchShiftRequests();
				setDeleteRequestConfirmOpen(false);
				setRequestToDelete(null);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to delete shift request');
			},
		}
	);

	// Handler for opening publish modal
	const handleOpenPublishModal = () => {
		if (!hasShiftsInWeek) {
			setOpenNoShiftsPublishDialog(true);
			return;
		}

		setOpenPublishModal(true);
	};

	// Handler for publishing shifts
	const handlePublishShifts = (startDate: string, endDate: string) => {
		if (!workplaceId) return;

		publishShifts({
			workplace: `/api/workplaces/${workplaceId}`,
			start: startDate,
			end: endDate,
		});

		setOpenPublishModal(false);
	};

	const { data: _eventTypes } = useQuery('eventTypes', () => fetchAllEventTypes(), {
		staleTime: 300000,
	});

	const _eventTypesMap = useMemo(() => {
		if (!_eventTypes?.member) return {};
		return _eventTypes.member.reduce((map: any, eventType: any) => {
			map[eventType['@id']] = eventType;
			return map;
		}, {});
	}, [_eventTypes]);
	// Fetch shifts data
	const { refetch: refetchShifts, isLoading: isLoadingShifts, isFetched: isShiftsFetched } = useQuery(
		['shifts', workplaceId, fromDate, toDate],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate,
				toDate,
			}),
		{
			refetchInterval: 600000,
			refetchIntervalInBackground: false,
			enabled:
				!!workplaceId &&
				!!fromDate &&
				!!toDate &&
				!!workplaceUsers.length &&
				!!allDays,
			onSuccess: (data) => {
				const shifts = data?.member || [];
				const currentUserId = localStorage.getItem('userId');

				let filteredShifts = shifts;

				if (!canViewAllSchedules) {
					// Regular employees (Schedule - My only): see published house shifts
					// and their own published shifts. Unpublished shifts stay hidden.
					filteredShifts = shifts.filter((shift: Shift) => {
						if (!shift.published) {
							return false;
						}

						// Show published house shifts (unassigned) to everyone
						if (!shift.employee) {
							return true;
						}

						// Show published shifts if assigned to current user
						const employeeId =
							shift.employee?.id?.toString() ||
							shift.employee?.toString().replace('/api/employees/', '') ||
							shift.employee?.toString();

						return employeeId === currentUserId;
					});
				}

				// Sort shifts by start time, then by shift id.
				// OT/warning status is derived reactively below so it stays current
				// when shifts, settings, users, or week bounds change.
				const sortedShifts = [...filteredShifts].sort((a, b) => {
					const timeDiff = moment(a.startAt).valueOf() - moment(b.startAt).valueOf();
					if (timeDiff !== 0) return timeDiff;
					return (a.id || 0) - (b.id || 0);
				});

				setAllShifts(
					sortedShifts.map((shift: Shift): CalendarShift => {
						const employeeData = shift.employee?.['@id']
							? workplaceUsersMap.get(shift.employee['@id'])
							: null;

						const minorAge = generalSettings?.ageOfMinor ?? 18;
						let isMinor = false;
						if (employeeData?.dateOfBirth) {
							const age = moment().diff(moment(employeeData.dateOfBirth), 'years');
							isMinor = age < minorAge;
						}

						const enhancedEmployee = shift.employee
							? {
									...shift.employee,
									isMinor: isMinor,
									dateOfBirth: employeeData?.dateOfBirth,
								}
							: shift.employee;

						return {
							...shift,
							employee: enhancedEmployee,
							id: shift.id,
							start: moment(shift.startAt).toDate(),
							end: moment(shift.endAt).toDate(),
							shift_id: shift.id,
							editable: canManageShifts,
							role: shift.jobCode?.label,
							background_color: shift.jobCode?.backgroundColor,
							text_color: shift.jobCode?.textColor,
							color: shift.jobCode?.backgroundColor,
							published: shift.published,
							publishedAt: shift.publishedAt,
							publishedBy: shift.publishedBy,
							createdBy: shift.createdBy,
							employeeStatusData: null,
							employeeTimeOff: employeeData?.userTimeOff || [],
							employeeAvailability: employeeData?.userAvailability || [],
						};
					})
				);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to fetch shifts');
			},
		}
	);

	const shiftsQueryEnabled =
		!!workplaceId &&
		!!fromDate &&
		!!toDate &&
		!!workplaceUsers.length &&
		!!allDays.length;

	const isShiftsLoading =
		shiftsQueryEnabled && (!isShiftsFetched || isLoadingShifts);

	// Recalculate OT/availability warnings whenever shifts or their inputs change
	const shiftsWithStatus = useMemo(() => {
		if (!fromDate || !toDate || !allShifts.length) return allShifts;

		return enrichShiftsWithEmployeeStatus({
			shifts: allShifts,
			fromDate,
			toDate,
			generalSettings,
			workplaceUsersMap,
			allDays,
		});
	}, [allShifts, fromDate, toDate, generalSettings, workplaceUsersMap, allDays]);

	// Check shift publish state for the current business week
	const { hasShiftsInWeek, hasUnpublishedShifts } = useMemo(() => {
		if (!fromDate || !startOfWeek) {
			return { hasShiftsInWeek: false, hasUnpublishedShifts: false };
		}

		const dayMap: { [key: string]: number } = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const startDayOfWeek = dayMap[startOfWeek] ?? 1;
		const currentDate = moment(fromDate);
		const currentDayOfWeek = currentDate.day();

		let daysBack = currentDayOfWeek - startDayOfWeek;
		if (daysBack < 0) {
			daysBack += 7;
		}

		const businessWeekStart = currentDate.clone().subtract(daysBack, 'days').startOf('day');
		const businessWeekEnd = businessWeekStart.clone().add(6, 'days').endOf('day');

		const shiftsInWeek = allShifts.filter((shift) => {
			const shiftStart = moment(shift.startAt);
			return shiftStart.isBetween(businessWeekStart, businessWeekEnd, null, '[]');
		});

		return {
			hasShiftsInWeek: shiftsInWeek.length > 0,
			hasUnpublishedShifts: shiftsInWeek.some((shift) => !shift.published),
		};
	}, [allShifts, fromDate, startOfWeek]);

	const { refetch: refetchEvents, isLoading: isLoadingEvents } = useQuery(
		['events-schedule-page', workplaceId, fromDate, toDate],
		() =>
			fetchAllEvents({
				workplaceId,
				fromDate: fromDate,
				toDate: toDate,
			}),
		{
			refetchInterval: 600000, // 10 min interval for refresh
			refetchIntervalInBackground: false,
			enabled: !!fromDate && !!toDate,
			onSuccess: async (data) => {
				const events = data?.member || [];
				setAllEvents(
					events.map((event: any) => {
						return {
							...event,
							// Map API fields to expected format
							start: event.startDate,
							end: event.endDate,
							title: event.name,
							resource: event?.type,
							event_type: event?.type,
						};
					})
				);
			},
		}
	);

	// Fetch holidays for the current date range
	const { refetch: _refetchHolidays, isLoading: isLoadingHolidays } = useQuery(
		['holidays', workplaceId, fromDate, toDate],
		() =>
			fetchHolidays({
				fromDate: moment(fromDate).format('YYYY-MM-DD'),
				toDate: moment(toDate).format('YYYY-MM-DD'),
			}),
		{
			staleTime: 3600000, // 1 hour
			enabled: !!fromDate && !!toDate,
			onSuccess: (holidays) => {
				// Store raw holiday data for the HolidaysList component and DesktopPage
				setRawHolidays(holidays);
			},
			onError: (error: any) => {
				console.error('Error fetching holidays:', error);
				// Don't show error toast as holidays are optional
			},
		}
	);

	const handleShiftDragUpdate = (event: any): boolean => {
		// Check if user has permission to edit shifts
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return false;
		}

		// Extract the actual event data
		const eventData = event.event || event;

		// Check if the new date is in a past week
		if (isDateInPastWeek(eventData.start || eventData.startAt, businessWeekStart, startOfWeek)) {
			showToast('error', 'Cannot move shifts to past weeks');
			return false;
		}

		// Extract shift ID - could be in id or shift_id
		const shiftId = eventData.shift_id || eventData.id;
		if (!shiftId) {
			showToast('error', 'Invalid shift data');
			return false;
		}

		const startAt = moment(eventData.start || eventData.startAt).format('YYYY-MM-DDTHH:mm:ss');
		const endAt = moment(eventData.end || eventData.endAt).format('YYYY-MM-DDTHH:mm:ss');
		const shiftLengthError = getShiftLengthValidationError(startAt, endAt, {
			minShiftLength: generalSettings?.minShiftLength,
			maxShiftLength: generalSettings?.maxShiftLength,
		});
		if (shiftLengthError) {
			showToast('error', shiftLengthError);
			return false;
		}

		// Get original dates (before drag) for metrics update
		const originalStartDate = eventData.originalStartAt
			? moment(eventData.originalStartAt).format('YYYY-MM-DD')
			: null;
		const originalEndDate = eventData.originalEndAt
			? moment(eventData.originalEndAt).format('YYYY-MM-DD')
			: null;

		// Build the payload for the API
		// event.start and event.end are Date objects - send as-is without timezone conversion
		const payload: IShiftPayload = {
			workplace: `api/workplaces/${workplaceId}`,
			startAt,
			endAt,
			employee:
				eventData.employee?.['@id'] ||
				(eventData.employee?.id ? `api/users/${eventData.employee.id}` : null),
			jobCode:
				eventData.jobCode?.['@id'] ||
				(eventData.jobCode?.id ? `api/job_codes/${eventData.jobCode.id}` : null),
			startOfWeek: businessWeekStart ? moment(businessWeekStart).format('YYYY-MM-DD') : undefined,
		};

		// Call the API to update the shift with original dates for metrics tracking
		updateShiftOnDrag({
			id: shiftId,
			body: payload,
			originalStartDate,
			originalEndDate,
		});
		return true;
	};

	const openEditModal = (event: any) => {
		// Check if user has permission to edit shifts
		if (calendarType === 'schedule' && !canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return;
		}

		if (calendarType === 'schedule' && !isWorkplaceUsersReady) {
			return;
		}

		// Check if the shift date is in a past week
		if (
			calendarType === 'schedule' &&
			isDateInPastWeek(event.start || event.startAt, businessWeekStart, startOfWeek)
		) {
			showToast('error', 'Cannot edit shifts on past weeks');
			return;
		}

		setEditEvent(event);

		// Set the date to scroll to when returning from edit
		if (event.start) {
			setLastFocusedDate(event.start);
		}

		switch (calendarType) {
			case 'schedule':
				setCurrentView('edit-shift');
				break;
			case 'event':
				_openEditEventModal();
				break;
			default:
				return;
		}
	};

	const openAddModal = (event: any) => {
		// Check if user has permission to create shifts
		if (calendarType === 'schedule' && !canManageShifts) {
			showToast('error', 'You do not have permission to create shifts');
			return;
		}

		if (calendarType === 'schedule' && !isWorkplaceUsersReady) {
			return;
		}

		// Determine the date to check
		const dateToCheck =
			event.action === 'drag' || event.action === 'externalDrop' ? event.event.start : event.date;

		// Check if the date is in a past week (before the current business week)
		if (
			calendarType === 'schedule' &&
			isDateInPastWeek(dateToCheck, businessWeekStart, startOfWeek)
		) {
			showToast('error', 'Cannot add shifts on past weeks');
			return;
		}

		setAddNewEvent(undefined);

		if (event.action === 'drag' || event.action === 'externalDrop') {
			setAddNewEvent({
				start_at: event.event.start,
				resource: event.event.resource,
				end_at: event.event.end,
				...event.event,
			});
			// Set the date to scroll to when returning
			setLastFocusedDate(event.event.start);
		} else {
			// Determine if we should prefill employee or job code based on calendar view
			let employeeFromResource = null;
			let jobCodeFromResource = null;

			// In day view, resource is job code ID
			if (calendarView === 'day') {
				const jobCodeId = event.jobCode || event.resource;
				// Find the job code by ID from jobCodesData
				if (jobCodeId && jobCodesData) {
					const jobCode = jobCodesData.find(
						(jc: any) => jc.id === jobCodeId || jc.id === Number(jobCodeId)
					);
					if (jobCode) {
						jobCodeFromResource = {
							'@id': jobCode['@id'] || `/api/job_codes/${jobCode.id}`,
							id: jobCode.id,
							label: jobCode.label,
							jobNumber: jobCode.jobNumber,
							backgroundColor: jobCode.backgroundColor,
							textColor: jobCode.textColor,
						};
					}
				}
			}

			// In week/workweek view, resource is employee ID
			if (calendarView === 'week' || calendarView === 'workweek') {
				const resourceId = event.resource;
				// Skip if it's the "open-shifts" resource (house shifts)
				if (resourceId && resourceId !== 'open-shifts') {
					// Find the employee by ID from workplaceUsers
					const employee = workplaceUsers?.find(
						(user) => user.id === resourceId || user['@id'] === `/api/users/${resourceId}`
					);
					if (employee) {
						employeeFromResource = employee;
					}
				}
			}

			// For current week, determine the appropriate default date
			let startDate = event.date;
			if (businessWeekStart) {
				const today = moment().startOf('day');
				const weekStart = moment(businessWeekStart).startOf('day');
				const weekEnd = weekStart.clone().add(6, 'days').endOf('day');
				const isCurrentWeek = today.isBetween(weekStart, weekEnd, null, '[]');

				if (isCurrentWeek) {
					// For current week: always use today as the default start date
					startDate = today.toDate();
				}
			}

			// Check if the event has time information (from cell click) or just date (from add button)
			const eventMoment = moment(event.date);
			const hasTimeInfo = eventMoment.hours() !== 0 || eventMoment.minutes() !== 0;

			let startDateWithTime: Date;

			if (hasTimeInfo) {
				// Cell click - use the clicked time
				startDateWithTime = eventMoment.toDate();
			} else {
				// Add button click - use working hours start time as default
				const workingHoursStart = getWorkingHoursForDate(startDate);
				const [hours, minutes] = workingHoursStart.split(':').map(Number);
				startDateWithTime = moment(startDate)
					.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
					.toDate();
			}

			setAddNewEvent({
				start_at: startDateWithTime,
				resource: event.resource,
				employeeFromResource: employeeFromResource,
				jobCodeFromResource: jobCodeFromResource,
			});
			// Set the date to scroll to when returning
			setLastFocusedDate(event.date);
		}

		// Open the appropriate view/modal based on calendar type
		switch (calendarType) {
			case 'schedule':
				setCurrentView('add-shift');
				break;
			case 'event':
				_openAddEventModal();
				break;
			default:
				_openAddEventModal();
				break;
		}
	};

	// Employee request modal handlers
	const handleOpenEmployeeRequest = () => {
		setEditRequest(null);
		setCurrentView('employee-request');
	};

	// Request detail view handlers
	const handleOpenRequestDetail = (request: ShiftRequest) => {
		setSelectedRequestForDetail(request);
	};

	const handleCloseRequestDetail = () => {
		setSelectedRequestForDetail(null);
	};

	const handleEditRequest = (request: ShiftRequest) => {
		setSelectedRequestForDetail(null);
		setEditRequest(request);
		setCurrentView('employee-request');
	};

	const handleDeleteRequestClick = (request: ShiftRequest) => {
		setRequestToDelete(request.id);
		setDeleteRequestConfirmOpen(true);
	};

	const handleApproveRequestFromDetail = async () => {
		if (selectedRequestForDetail) {
			await handleApproveRequest(selectedRequestForDetail);
			handleCloseRequestDetail();
		}
	};

	const handleDenyRequestFromDetail = async () => {
		if (selectedRequestForDetail) {
			await handleDenyRequest(selectedRequestForDetail);
			handleCloseRequestDetail();
		}
	};

	const handleApproveRequest = async (request: ShiftRequest) => {
		if (!request?.id || processingRequestId === request.id) {
			return;
		}

		try {
			setProcessingRequestId(request.id);
			const requestType = request?.type?.label || '';

			await approveOrDenyShiftRequest(request.id, requestType, true);
			queryClient.invalidateQueries(['shift-requests']);
			refetchShiftRequests();
			refetchShifts();

			// If it's a time-off request, refetch users to update their time-off data
			if (requestType && requestType.toLowerCase().includes('time')) {
				queryClient.invalidateQueries(['users-list', workplaceId]);
			}
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to approve request');
		} finally {
			setProcessingRequestId(null);
		}
	};

	const handleDenyRequest = async (request: ShiftRequest) => {
		if (!request?.id || processingRequestId === request.id) {
			return;
		}

		try {
			setProcessingRequestId(request.id);
			const requestType = request?.type?.label || '';

			await approveOrDenyShiftRequest(request.id, requestType, false);
			queryClient.invalidateQueries(['shift-requests']);
			refetchShiftRequests();
			refetchShifts();
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to deny request');
		} finally {
			setProcessingRequestId(null);
		}
	};

	const confirmDeleteRequest = () => {
		if (requestToDelete) {
			deleteShiftRequestMutation(requestToDelete);
		}
	};

	const { mutate: deleteTimeOffMutation, isLoading: isDeletingTimeOff } = useMutation(
		(userTimeOffId: number) => deleteUserTimeOff(userTimeOffId),
		{
			onSuccess: (_data, userTimeOffId) => {
				setRemovedUserTimeOffIds((current) => {
					const next = new Set(current);
					next.add(userTimeOffId);
					return next;
				});
				queryClient.setQueryData(
					['workplace-employees', workplaceId],
					(currentUsers: IUsers[] | undefined) =>
						removeUserTimeOffFromUsers(currentUsers, userTimeOffId)
				);
				queryClient.invalidateQueries(['shift-requests']);
				queryClient.invalidateQueries(['workplace-employees', workplaceId]);
				queryClient.invalidateQueries(['users-list', workplaceId]);
				refetchShiftRequests();
				refetchWorkplaceEmployees();
				setIsDeleteTimeOffDialogOpen(false);
				setTimeOffToDelete(null);
				setSelectedTimeOffRequest(null);
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to delete time off');
			},
		}
	);

	const shiftsMap = useMemo(() => buildShiftsMap(allShifts), [allShifts]);

	// Create calendar events for approved time off requests
	const approvedTimeOffEvents = useMemo(() => {
		if (!shiftRequestsData?.['member']) return [];

		const weekAnchor = businessWeekStart || fromDate;
		const weekStart = moment(weekAnchor).startOf('day');
		const weekEnd = moment(weekAnchor).add(7, 'days').startOf('day');

		// Filter for approved time off requests that overlap the visible business week.
		// Out-of-range requests must not create a phantom "timeoff" resource.
		const approvedTimeOff = shiftRequestsData['member'].filter((request: ShiftRequest) => {
			const isTimeOff =
				request.type?.label?.toLowerCase().includes('time off') ||
				request.type?.label?.toLowerCase().includes('time-off');

			if (!isTimeOff) return false;
			if (request.employeeRequestConfirmed !== true) return false;
			if (!request.startAt || !request.endAt) return false;

			const requestStart = moment(request.startAt);
			const requestEnd = moment(request.endAt);
			return requestEnd.isAfter(weekStart) && requestStart.isBefore(weekEnd);
		});

		// Transform approved time off requests into calendar events.
		// Use inclusive day boundaries so multi-day ranges (e.g. 3 days) cover every
		// selected day; week view keeps the full span for isTimeOff events.
		const isWeekView = calendarView === 'week' || calendarView === 'workweek';

		return approvedTimeOff.flatMap((request: ShiftRequest) => {
			const employeeId = request.employeeRequest?.id;
			const employeeName = request.employeeRequest
				? `${request.employeeRequest.firstName} ${request.employeeRequest.lastName}`
				: 'Unknown Employee';
			const workplaceUser = workplaceUsers.find(
				(user) => String(user.id) === String(employeeId)
			);
			const userTimeOffId = getLiveUserTimeOffId({
				requestUserTimeOff: request.userTimeOff,
				employeeTimeOffs: workplaceUser?.userTimeOff,
				startAt: request.startAt,
				endAt: request.endAt,
			});

			if (
				!shouldDisplayCalendarTimeOff({
					userTimeOffId,
					removedUserTimeOffIds,
				})
			) {
				return [];
			}

			if (
				!canViewCalendarTimeOff({
					event: {
						employee: request.employeeRequest,
						timeOffRequest: request,
					},
					currentUserId: scheduleUserId,
					canViewAllSchedules,
				})
			) {
				return [];
			}

			const isTimed = isTimedTimeOff(request.startAt, request.endAt, request.allDay);
			const range = resolveTimeOffRange(request.startAt, request.endAt, request.allDay);
			const start =
				isTimed && !isWeekView
					? moment(request.startAt).toDate()
					: (range?.start ?? moment(request.startAt).startOf('day')).toDate();
			const end =
				isTimed && !isWeekView
					? moment(request.endAt).toDate()
					: (range?.end ?? moment(request.endAt).endOf('day')).toDate();

			const baseEvent = {
				title: isWeekView ? 'Time Off' : employeeName,
				start,
				end,
				startAt: request.startAt,
				endAt: request.endAt,
				color: '#FF9800',
				background_color: '#FF9800',
				text_color: '#FFFFFF',
				editable: false,
				isTimeOff: true,
				timeOffRequest: request,
				employee: request.employeeRequest || null,
				userTimeOffId,
			};

			if (!isWeekView) {
				return [
					{
						...baseEvent,
						id: `timeoff-${request.id}`,
						resource: 'timeoff',
					},
				];
			}

			const departmentResourceIds = findEmployeeResourceIds(resourcesToPass, employeeId);
			const resourceIds =
				departmentResourceIds.length > 0
					? departmentResourceIds
					: [employeeId || 'unknown'];

			return resourceIds.map((resourceId) => ({
				...baseEvent,
				id: `timeoff-${request.id}-${resourceId}`,
				resource: resourceId,
			}));
		});
	}, [
		shiftRequestsData,
		calendarView,
		resourcesToPass,
		workplaceUsers,
		removedUserTimeOffIds,
		canViewAllSchedules,
		scheduleUserId,
		businessWeekStart,
		fromDate,
	]);

	const calendarResources = useMemo(() => {
		if (!resourcesToPass) return resourcesToPass;

		// Day view always pins a Time Off lane at the top (even when empty),
		// matching pre-v7 schedule behavior.
		const isDayView = calendarView === 'day';
		if (!isDayView) return resourcesToPass;
		if (resourcesToPass.some((resource) => String(resource.id) === 'timeoff')) {
			return resourcesToPass;
		}

		return [
			{
				id: 'timeoff',
				name: 'Time Off',
				displayName: 'Time Off',
				sortOrder: -1,
			},
			...resourcesToPass.map((resource, index) => ({
				...resource,
				sortOrder: (resource.sortOrder ?? index) + 1,
			})),
		];
	}, [resourcesToPass, calendarView]);

	const calendarShiftEvents = useMemo(() => {
		const shiftsForCalendar = !jobCodesData
			? shiftsWithStatus
			: filterShiftsByDepartments(shiftsWithStatus, jobCodesData, selectedDepartmentIds);

		return shiftsForCalendar.map((shift) => {
			const isWeekView = calendarView === 'week' || calendarView === 'workweek';
			const positionLabel =
				typeof shift.position === 'object' && shift.position?.name
					? shift.position.name
					: null;
			const weekTitleBase = shift.jobCode?.label || 'Shift';
			const weekTitle = positionLabel ? `${weekTitleBase} - ${positionLabel}` : weekTitleBase;

			return {
				...shift,
				resource: resolveShiftResourceId({
					shift,
					calendarView,
					jobCodes: jobCodesData || [],
					departments,
				}),
				title: isWeekView
					? weekTitle
					: shift.employee
						? `${shift.employee.firstName} ${shift.employee.lastName}`
						: 'House Shift',
			};
		});
	}, [
		shiftsWithStatus,
		selectedDepartmentIds,
		calendarView,
		jobCodesData,
		departments,
	]);

	const getEmployeeName = (
		employee:
			| ShiftRequest['employeeRequest']
			| ShiftRequest['employeeAssigned']
			| ShiftRequest['createdBy']
	) => {
		if (!employee) return 'N/A';
		return (
			`${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.email || 'N/A'
		);
	};

	const getShiftDates = (request: ShiftRequest) =>
		formatRequestScheduleDates(request, { shiftsMap, allDays });

	const pendingEmployeeRequests = useMemo(() => {
		const members = shiftRequestsData?.['member'] || [];

		return members
			.filter((request: ShiftRequest) => {
				const isTradeRequest = request?.type?.label?.toLowerCase()?.includes('trade');
				const isOwnRequest = canManageAllRequests || request?.createdBy?.id === _auth?.user?.id;
				const isPickupRequest = request?.type?.label?.toLowerCase()?.includes('pickup');
				let shouldExcludeExpiredHouseShift = false;

				if (isPickupRequest && request.shiftRequest) {
					const shift = lookupShift(request.shiftRequest, shiftsMap);
					const shiftEnd = shift?.end || shift?.endAt || shift?.end_at;

					if (shift && !shift.employee && shiftEnd) {
						shouldExcludeExpiredHouseShift = moment(shiftEnd).isBefore(moment());
					}
				}

				return (
					(request.employeeRequestConfirmed === null ||
						request.employeeRequestConfirmed === undefined) &&
					!isTradeRequest &&
					isOwnRequest &&
					!shouldExcludeExpiredHouseShift
				);
			})
			.sort((a: ShiftRequest, b: ShiftRequest) => {
				const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
				const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
				return dateA - dateB;
			});
	}, [shiftRequestsData, canManageAllRequests, _auth?.user?.id, shiftsMap]);

	const handleEditStats = () => {
		if (!canEditShifts) return;
		setCurrentView('edit-stats');
	};

	const handleCopySchedule = () => {
		setCurrentView('copy-schedule');
	};

	const handleBackToSchedule = () => {
		setCurrentView('scheduler');
		setEditRequest(null);
	};

	// Watch for business week changes from HeaderBar via context
	useEffect(() => {
		if (!businessWeekStart) return;

		const weekStartDate = moment(businessWeekStart).startOf('day');
		const weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');

		// Update the date range based on calendar view
		// let startDate, endDate;

		// Both day view and week view now show the entire business week
		// Day view has hourly granularity, week view has larger time blocks
		// Use calendar day boundaries (00:00:00 to 23:59:59) without timezone conversion
		const formatedFirstDay = weekStartDate.format('YYYY-MM-DD') + ' 00:00:00';
		const formatedLastDay = weekEndDate.format('YYYY-MM-DD') + ' 23:59:59';

		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);

		// Reset last focused date when business week changes via header controls
		// This ensures the calendar scrolls to the start of the new week
		setLastFocusedDate(null);
	}, [businessWeekStart]);

	// Clean up invalid lastFocusedDate when it falls outside the business week
	useEffect(() => {
		if (!lastFocusedDate || !businessWeekStart) return;

		const weekStartDate = moment(businessWeekStart).startOf('day');
		const weekEndDate = weekStartDate.clone().add(6, 'days').endOf('day');
		const focusedDateMoment = moment(lastFocusedDate).startOf('day');

		// If lastFocusedDate is outside the current business week, clear it
		if (!focusedDateMoment.isBetween(weekStartDate, weekEndDate, null, '[]')) {
			setLastFocusedDate(null);
		}
	}, [lastFocusedDate, businessWeekStart]);

	const updateTimeRange = (firstday, lastday) => {
		// Prevent updateTimeRange from changing dates if firstday matches businessWeekStart
		// This prevents onPageChange from overriding the business week when calendar auto-scrolls
		if (businessWeekStart) {
			const _businessWeekStartMoment = moment(businessWeekStart).startOf('day');
			const _firstdayMoment = moment(firstday).startOf('day');

			// If firstday is the same as businessWeekStart, skip update (calendar is just repositioning)
			// if (businessWeekStartMoment.isSame(firstdayMoment, 'day')) {
			// 	return;
			// }
		}

		// Use calendar day boundaries (00:00:00 to 23:59:59) without timezone conversion
		const formatedFirstDay = moment(firstday).format('YYYY-MM-DD') + ' 00:00:00';
		const formatedLastDay = moment(lastday).format('YYYY-MM-DD') + ' 23:59:59';

		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);

		// Check if the user is looking at the next week to enable the publish schedule button
		const firstDayOfSelectedWeek = moment().startOf('week');
		const nextWeekStart = moment(firstDayOfSelectedWeek).add(7, 'days');
		const nextWeekEnd = moment(firstDayOfSelectedWeek).add(14, 'days');

		const isPublishable = moment(firstday).isBetween(nextWeekStart, nextWeekEnd, null, '[]');
		setIsPublishable(isPublishable);
	};

	const _closeEditModalHandler = () => {
		// Only handle events now - shifts use full-screen approach
		if (calendarType === 'event') {
			refetchEvents();
			_closeEditEventModal();
		}
	};

	const handleBackToScheduler = () => {
		setCurrentView('scheduler');
		setAddNewEvent(undefined);
		setEditEvent(null);
		refetchShifts();
	};

	const buildShiftPayload = useCallback(
		(
			shift: any,
			overrides: Partial<{
				employee: string | null;
				jobCode: string | null;
				position: string | null;
				startAt: string;
				endAt: string;
			}> = {}
		): IShiftPayload => {
			const startSource = overrides.startAt || shift.startAt || shift.start;
			const endSource = overrides.endAt || shift.endAt || shift.end;

			return {
				workplace: `api/workplaces/${workplaceId}`,
				startAt: moment(startSource).format('YYYY-MM-DDTHH:mm:ss'),
				endAt: moment(endSource).format('YYYY-MM-DDTHH:mm:ss'),
				employee:
					overrides.employee !== undefined
						? overrides.employee
						: shift.employee?.['@id'] ||
							(shift.employee?.id ? `api/users/${shift.employee.id}` : null),
				jobCode:
					overrides.jobCode !== undefined
						? overrides.jobCode
						: shift.jobCode?.['@id'] ||
							(shift.jobCode?.id ? `api/job_codes/${shift.jobCode.id}` : null),
				position:
					overrides.position !== undefined
						? overrides.position
						: shift.position?.['@id'] ||
							(typeof shift.position === 'string' ? shift.position : null),
				startOfWeek: businessWeekStart ? moment(businessWeekStart).format('YYYY-MM-DD') : undefined,
			};
		},
		[workplaceId, businessWeekStart]
	);

	const closeShiftSchedulerDialog = () => {
		setShiftSchedulerDialog((prev) => ({
			...prev,
			open: false,
			shift: undefined,
			createContext: undefined,
		}));
	};

	const handleCreateShiftByDrag = ({
		start,
		end,
		resourceId,
	}: {
		start: Date;
		end: Date;
		resourceId: string;
	}) => {
		if (isDepartmentHeaderResourceId(resourceId)) {
			return;
		}

		if (!canManageShifts) {
			showToast('error', 'You do not have permission to create shifts');
			return;
		}

		if (!isWorkplaceUsersReady) {
			return;
		}

		if (isDateInPastWeek(start, businessWeekStart, startOfWeek)) {
			showToast('error', 'Cannot add shifts on past weeks');
			return;
		}

		const shiftLengthSettings = {
			defaultShiftLengthHours: generalSettings?.defaultShiftLengthHours,
			minShiftLength: generalSettings?.minShiftLength,
			maxShiftLength: generalSettings?.maxShiftLength,
		};

		const isWeekView = calendarView === 'week' || calendarView === 'workweek';
		let startAt = start;
		let endAt = end;

		if (isWeekView) {
			const workingHoursTime = workplaceWorkingHoursStart || startOfDay || '08:00';
			const [hours, minutes] = workingHoursTime.split(':').map(Number);
			startAt = moment(start)
				.startOf('day')
				.set({ hour: hours || 0, minute: minutes || 0, second: 0, millisecond: 0 })
				.toDate();
			endAt = moment(startAt)
				.add(getDefaultShiftDurationHours(shiftLengthSettings), 'hours')
				.toDate();
		} else {
			const shiftLengthError = getShiftLengthValidationError(
				moment(start).format('YYYY-MM-DDTHH:mm:ss'),
				moment(end).format('YYYY-MM-DDTHH:mm:ss'),
				shiftLengthSettings
			);
			if (shiftLengthError) {
				showToast('error', shiftLengthError);
				return;
			}
		}

		setShiftSchedulerDialog({
			open: true,
			mode: isWeekView ? 'create-week' : 'create-day',
			createContext: {
				startAt,
				endAt,
				jobCodeId: isWeekView ? undefined : resourceId,
				employeeId: isWeekView ? parseWeekViewEmployeeId(resourceId) : undefined,
			},
		});
	};

	const handleQuickSaveDayUser = (employeeId: string | null) => {
		const shift = shiftSchedulerDialog.shift;
		if (!shift) return;

		const shiftId = shift.shift_id || shift.id;
		if (!shiftId) return;

		updateShiftQuick({
			id: shiftId,
			body: buildShiftPayload(shift, { employee: employeeId }),
			keepOpen: true,
			metricDates: collectShiftMetricDates(shift.startAt, shift.start),
		});
	};

	const handleQuickSaveDayPosition = (position: string | null) => {
		const shift = shiftSchedulerDialog.shift;
		if (!shift) return;

		const shiftId = shift.shift_id || shift.id;
		if (!shiftId) return;

		updateShiftQuick({
			id: shiftId,
			body: buildShiftPayload(shift, { position }),
			keepOpen: true,
			metricDates: collectShiftMetricDates(shift.startAt, shift.start),
		});
	};

	const handleQuickSaveWeekFields = (fields: {
		jobCode: string;
		position: string | null;
		startAt: string;
		endAt: string;
	}) => {
		const shift = shiftSchedulerDialog.shift;
		if (!shift) return;

		const shiftId = shift.shift_id || shift.id;
		if (!shiftId) return;

		const shiftLengthError = getShiftLengthValidationError(fields.startAt, fields.endAt, {
			minShiftLength: generalSettings?.minShiftLength,
			maxShiftLength: generalSettings?.maxShiftLength,
		});
		if (shiftLengthError) {
			showToast('error', shiftLengthError);
			return;
		}

		updateShiftQuick({
			id: shiftId,
			body: buildShiftPayload(shift, {
				jobCode: fields.jobCode,
				position: fields.position,
				startAt: fields.startAt,
				endAt: fields.endAt,
			}),
			metricDates: collectShiftMetricDates(
				shift.startAt,
				shift.start,
				fields.startAt
			),
		});
	};

	const handleCreateDayShift = (employeeId: string | null, position?: string | null) => {
		const context = shiftSchedulerDialog.createContext;
		if (!context?.jobCodeId) return;

		const startAt = moment(context.startAt).format('YYYY-MM-DDTHH:mm:ss');
		const endAt = moment(context.endAt).format('YYYY-MM-DDTHH:mm:ss');
		const shiftLengthError = getShiftLengthValidationError(startAt, endAt, {
			minShiftLength: generalSettings?.minShiftLength,
			maxShiftLength: generalSettings?.maxShiftLength,
		});
		if (shiftLengthError) {
			showToast('error', shiftLengthError);
			return;
		}

		createShiftFromDialog({
			workplace: `api/workplaces/${workplaceId}`,
			startAt,
			endAt,
			employee: employeeId,
			jobCode: `api/job_codes/${context.jobCodeId}`,
			position: position ?? null,
			startOfWeek: businessWeekStart ? moment(businessWeekStart).format('YYYY-MM-DD') : undefined,
		});
	};

	const handleCreateWeekShift = (fields: {
		jobCode: string;
		position: string | null;
		startAt: string;
		endAt: string;
	}) => {
		const context = shiftSchedulerDialog.createContext;
		if (!context) return;

		const shiftLengthError = getShiftLengthValidationError(fields.startAt, fields.endAt, {
			minShiftLength: generalSettings?.minShiftLength,
			maxShiftLength: generalSettings?.maxShiftLength,
		});
		if (shiftLengthError) {
			showToast('error', shiftLengthError);
			return;
		}

		const employeeId = parseWeekViewEmployeeId(context.employeeId);
		createShiftFromDialog({
			workplace: `api/workplaces/${workplaceId}`,
			startAt: moment(fields.startAt).format('YYYY-MM-DDTHH:mm:ss'),
			endAt: moment(fields.endAt).format('YYYY-MM-DDTHH:mm:ss'),
			employee: employeeId ? `api/users/${employeeId}` : null,
			jobCode: fields.jobCode,
			position: fields.position,
			startOfWeek: businessWeekStart ? moment(businessWeekStart).format('YYYY-MM-DD') : undefined,
		});
	};

	const handleOpenFullEditFromDialog = () => {
		const shift = shiftSchedulerDialog.shift;
		if (shift?.start || shift?.startAt) {
			setLastFocusedDate(shift.start || shift.startAt);
		}
		setShiftSchedulerDialog((prev) => ({ ...prev, open: false }));
		setCurrentView('edit-shift');
	};

	const handleTimeOffClick = (event: any) => {
		const request = getTimeOffRequestFromEvent(event);
		if (!request) return;
		setSelectedTimeOffRequest(request as ShiftRequest);
	};

	const handleEditShift = (event: any) => {
		if (event?.isTimeOff) {
			handleTimeOffClick(event);
			return;
		}

		// Check if user has permission to edit shifts
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to edit shifts');
			return;
		}

		if (!isWorkplaceUsersReady) {
			return;
		}

		// Check if the shift date is in a past week
		if (isDateInPastWeek(event.start || event.startAt, businessWeekStart, startOfWeek)) {
			showToast('error', 'Cannot edit shifts on past weeks');
			return;
		}

		setEditEvent(event);

		const isWeekView = calendarView === 'week' || calendarView === 'workweek';
		setShiftSchedulerDialog({
			open: true,
			mode: isWeekView ? 'quick-edit-week' : 'quick-edit-day',
			shift: event,
		});
	};

	const handleDeleteTimeOff = (event: any) => {
		if (
			!canDeleteCalendarTimeOff({
				event,
				currentUserId: scheduleUserId,
				canDeleteShifts,
				canManageAllRequests,
			})
		) {
			showToast('error', 'You do not have permission to delete this time off');
			return;
		}

		setTimeOffToDelete(event);
		setIsDeleteTimeOffDialogOpen(true);
	};

	const handleDeleteShift = (event: any) => {
		if (event?.isTimeOff) {
			handleDeleteTimeOff(event);
			return;
		}

		// Check if user has permission to delete shifts
		if (!canDeleteShifts) {
			showToast('error', 'You do not have permission to delete shifts');
			return;
		}

		// Check if the shift date is in a past week
		if (isDateInPastWeek(event.start || event.startAt, businessWeekStart, startOfWeek)) {
			showToast('error', 'Cannot delete shifts on past weeks');
			return;
		}

		// Store the shift to delete and show confirmation dialog
		shiftMetricDatesRef.current = collectShiftMetricDates(event.startAt, event.start);
		setShiftToDelete(event);
		setIsDeleteDialogOpen(true);
	};

	const handleCopyShift = (event: any) => {
		if (!canManageShifts) {
			showToast('error', 'You do not have permission to copy shifts');
			return;
		}

		// Check if the shift date is in a past week
		if (isDateInPastWeek(event.start || event.startAt, businessWeekStart, startOfWeek)) {
			showToast('error', 'Cannot copy shifts on past weeks');
			return;
		}

		// Build the payload for the new shift - same as original but employee is null (house shift)
		// event.start and event.end are Date objects - send as-is without timezone conversion
		const payload: IShiftPayload = {
			workplace: `api/workplaces/${workplaceId}`,
			startAt: moment(event.start).format('YYYY-MM-DDTHH:mm:ss'),
			endAt: moment(event.end).format('YYYY-MM-DDTHH:mm:ss'),
			employee: null, // Always create as house shift
			jobCode:
				event.jobCode?.['@id'] || (event.jobCode?.id ? `api/job_codes/${event.jobCode.id}` : null),
			startOfWeek: businessWeekStart ? moment(businessWeekStart).format('YYYY-MM-DD') : undefined,
		};
		copyShift(payload);
	};

	const confirmDeleteShift = () => {
		if (shiftToDelete) {
			// Use shift_id from calendar event or id from shift object
			const shiftId = shiftToDelete.shift_id || shiftToDelete.id;
			if (shiftId) {
				deleteShift(shiftId);
			}
		}
	};

	const confirmDeleteTimeOff = () => {
		const request = getTimeOffRequestFromEvent(timeOffToDelete);
		const employeeId = request?.employeeRequest?.id ?? timeOffToDelete?.employee?.id;
		const workplaceUser = workplaceUsers.find((user) => String(user.id) === String(employeeId));
		const userTimeOffId = getUserTimeOffIdFromEvent(timeOffToDelete, workplaceUser?.userTimeOff);

		if (userTimeOffId) {
			deleteTimeOffMutation(userTimeOffId);
			return;
		}

		showToast('error', 'Unable to find this time off record to delete');
	};

	const _closeAddModalHandler = () => {
		// Only handle events now - shifts use full-screen approach
		if (calendarType === 'event') {
			refetchEvents();
			_closeAddEventModal();
		}
	};

	const openRequestsModal = (event: any): any => {
		setRequestData(event);

		switch (event.shift_type.id) {
			case 2:
				_openTimeOffRequestModal();
				break;
			case 3:
				_openShiftPickModal();
				break;
			case 4:
				_openRequestAvailableModalOpen();
				break;
			case 5:
				_openCoverRequestModal();
				break;
			case 6:
				_openTradeRequestModal();
				break;
			default:
				return null;
		}
	};

	const getBusinessWeekRange = useCallback(() => {
		const dayMap: { [key: string]: number } = {
			Sunday: 0,
			Monday: 1,
			Tuesday: 2,
			Wednesday: 3,
			Thursday: 4,
			Friday: 5,
			Saturday: 6,
		};

		const startDayOfWeek = dayMap[startOfWeek] ?? 1;
		const currentDate = moment(fromDate);
		const currentDayOfWeek = currentDate.day();

		let daysBack = currentDayOfWeek - startDayOfWeek;
		if (daysBack < 0) {
			daysBack += 7;
		}

		const businessWeekStart = currentDate.clone().subtract(daysBack, 'days').startOf('day');
		const businessWeekEnd = businessWeekStart.clone().add(6, 'days').endOf('day');

		return { businessWeekStart, businessWeekEnd };
	}, [fromDate, startOfWeek]);

	const filteredEventsForWeek = useMemo(() => {
		const { businessWeekStart, businessWeekEnd } = getBusinessWeekRange();
		return allEvents.filter((event: any) => {
			const eventStart = moment(event.startDate || event.start);
			return eventStart.isBetween(businessWeekStart, businessWeekEnd, null, '[]');
		});
	}, [allEvents, getBusinessWeekRange]);

	const handleEventClick = (event: any) => {
		setSelectedEvent(event);
		setIsEventDialogOpen(true);
	};

	const handleCloseEventDialog = () => {
		setIsEventDialogOpen(false);
		setSelectedEvent(null);
	};

	const filterEvents = () => {
		const newArrOfEvents: MbscCalendarEvent[] = [];
		_schedulesBackup.forEach((event) => {
			return filters.forEach((filter) => {
				if (filter.id === event?.shift_type?.id) {
					if (filter.active) {
						newArrOfEvents.push(event);
					}
				}
			});
		});

		setAllSchedules(newArrOfEvents);
	};

	const _filterReset = (type: boolean) => {
		const newArr = filters.map((value) => {
			value.active = type;
			return {
				...value,
			};
		});

		setFilters(newArr);
		filterEvents();
	};

	// Handle different view states
	if (currentView === 'add-shift') {
		return (
			<AddShiftPage
				onBack={handleBackToScheduler}
				addNewShift={addNewEvent}
				generalSettings={generalSettings}
				workplaceUsers={workplaceUsers}
				daysData={allDays}
				weekStartDate={selectedWeekStartDate}
				workplaceWorkingHoursStart={workplaceWorkingHoursStart}
			/>
		);
	}

	if (currentView === 'edit-shift') {
		return (
			<AddShiftPage
				onBack={handleBackToScheduler}
				editShift={editEvent}
				generalSettings={generalSettings}
				workplaceUsers={workplaceUsers}
				daysData={allDays}
				weekStartDate={selectedWeekStartDate}
			/>
		);
	}

	// Handle employee request view
	if (currentView === 'employee-request') {
		return (
			<EmployeeRequestModal
				closeModal={handleBackToSchedule}
				refetchSchedules={() => {
					refetchShifts();
					refetchShiftRequests();
				}}
				currentWeekStart={fromDate ? moment(fromDate).toDate() : undefined}
				currentWeekEnd={toDate ? moment(toDate).toDate() : undefined}
				editRequest={editRequest}
			/>
		);
	}

	// Handle edit stats view
	if (currentView === 'edit-stats' && canEditShifts) {
		return <EditStatsPage onBack={handleBackToSchedule} startDate={fromDate} endDate={toDate} />;
	}

	// Handle copy schedule view
	if (currentView === 'copy-schedule') {
		return (
			<CopySchedulePage
				onBack={handleBackToSchedule}
				currentWeekStart={fromDate}
				currentWeekEnd={toDate}
				startOfWeek={startOfWeek}
			/>
		);
	}

	return (
		<>
			<section className="ScheduleContent">
			{/* Top requests / events box moved above the main Content to occupy full width */}
			<Grid
				container
				spacing={3}
				className="TopRequestsBox"
				direction="row"
				justifyContent={'space-between'}
			>
				<Grid className="TopRequestsSection" item xs={12} md={6}>
					<div className="TopSectionContainer">
						<div className="TopRequestsHeader">
							<h6>Employee Requests</h6>
							{canSubmitRequests && (
								<button
									onClick={handleOpenEmployeeRequest}
									onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgb(57, 80, 111)')}
									onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgb(68, 99, 136)')}
									style={{
										margin: '0 12px 0 auto',
										backgroundColor: 'rgb(68, 99, 136)',
										color: '#fff',
										height: '32px',
										borderRadius: '6px',
										fontSize: '14px',
										fontWeight: '500',
										fontFamily: 'Gotham',
										padding: '0 16px',
										border: 'none',
										cursor: 'pointer',
										textTransform: 'none',
									}}
								>
									Add
								</button>
							)}
						</div>
						<div className="TopRequestsBody">
							<DataTable
								columns={[
									{
										id: 'requestDate',
										header: 'Request Date',
										width: '18%',
										render: (request) =>
											request?.created_at
												? moment(request.created_at).tz(timezones).format('MM/DD/YYYY')
												: 'N/A',
									},
									{
										id: 'employee',
										header: 'Employee',
										width: '22%',
										render: (request) => getEmployeeName(request?.createdBy),
									},
									{
										id: 'type',
										header: 'Request Type',
										width: '20%',
										render: (request) => request?.type?.label || 'Unknown',
									},
									{
										id: 'scheduleDates',
										header: 'Schedule Dates',
										width: '24%',
										render: (request) => getShiftDates(request),
									},
								]}
								data={pendingEmployeeRequests}
								getRowKey={(request) => request.id}
								loading={isLoadingRequests}
								emptyMessage="No employee requests"
								onRowClick={handleOpenRequestDetail}
								actions={[
									{
										key: 'edit',
										label: 'Edit',
										icon: <Edit fontSize="small" />,
										hidden: (request) =>
											(request?.createdBy?.id || request?.employeeRequest?.id) !==
											_auth?.user?.id,
										onClick: handleEditRequest,
									},
									{
										key: 'delete',
										label: 'Delete',
										variant: 'danger',
										icon: <Delete fontSize="small" />,
										hidden: (request) =>
											(request?.createdBy?.id || request?.employeeRequest?.id) !==
											_auth?.user?.id,
										onClick: handleDeleteRequestClick,
									},
								]}
							/>
						</div>
					</div>
				</Grid>
				<Grid className="TopRequestsSection" item xs={12} md={6}>
					<div className="TopSectionContainer">
						<div className="TopRequestsHeader">
							<h6>Events this Week</h6>
						</div>
						<div className="TopRequestsBody">
							<DataTable
								columns={[
									{
										id: 'date',
										header: 'Date',
										width: '22%',
										render: (event) => moment(event.startDate || event.start).format('MM/DD/YYYY'),
									},
									{
										id: 'title',
										header: 'Title',
										width: '28%',
										render: (event) => event.name || event.title,
									},
									{
										id: 'type',
										header: 'Type',
										width: '20%',
										render: (event) => _eventTypesMap[event.type]?.label || 'Event',
									},
									{
										id: 'when',
										header: 'When',
										width: '30%',
										render: (event) =>
											`${moment(event.startDate || event.start).format('h:mm A')} - ${moment(
												event.endDate || event.end
											).format('h:mm A')}`,
									},
								]}
								data={filteredEventsForWeek}
								getRowKey={(event, index) => event.id || index}
								loading={isLoadingEvents}
								emptyMessage="No events"
								onRowClick={handleEventClick}
							/>
						</div>
						{/* Holiday Marquee - Attached at bottom */}
						{rawHolidays.length > 0 && !isLoadingHolidays && (
							<div className="holiday-marquee-container">
								<div className="holiday-marquee">
									<div className="holiday-marquee-content">
										{rawHolidays.map((holiday, index) => (
											<span key={index} className="holiday-marquee-item">
												🎉 {holiday.name} - {moment(holiday.holiday_date).format('MMM D, YYYY')}
											</span>
										))}
										{/* Duplicate for seamless loop */}
										{rawHolidays.map((holiday, index) => (
											<span key={`dup-${index}`} className="holiday-marquee-item">
												🎉 {holiday.name} - {moment(holiday.holiday_date).format('MMM D, YYYY')}
											</span>
										))}
									</div>
								</div>
							</div>
						)}
					</div>
				</Grid>
			</Grid>
			{/* Schedule Statistics Box - Full Width */}
			<Grid container spacing={2} style={{ marginBottom: '12px' }}>
				<Grid item xs={12}>
					<ScheduleStatsBox
						fromDate={fromDate}
						toDate={toDate}
						startOfWeek={startOfWeek}
						sales={statsWithMetrics.sales}
						splh={statsWithMetrics.splh}
						cplh={statsWithMetrics.cplh}
						hoursScheduled={statsWithMetrics.hoursScheduled}
						dailyOT={statsWithMetrics.dailyOT}
						weeklyOT={statsWithMetrics.weeklyOT}
						customers={statsWithMetrics.customers}
						onPublish={handleOpenPublishModal}
						onCopy={handleCopySchedule}
						onEdit={handleEditStats}
						canManageShifts={canManageShifts}
						canPublishShifts={canPublishShifts}
						canEditStats={canEditShifts}
						canViewProjectedMetrics={canViewAllSchedules}
						hasShiftsInWeek={hasShiftsInWeek}
						hasUnpublishedShifts={hasUnpublishedShifts}
					/>
				</Grid>
				{/* HolidaysList section - Hidden, now showing as marquee in Events section */}
				{/* {rawHolidays.length > 0 && (
					<Grid item xs={12} lg={4}>
						<HolidaysList holidays={rawHolidays} isLoading={isLoadingHolidays} />
					</Grid>
				)} */}
			</Grid>
			<Grid container spacing={2} className="schedule-grid">
				<Grid item xs={12}>
					<div className="draggable" style={{ position: 'relative' }}>
						{USE_FULLCALENDAR ? (
							<FullCalendarSchedule
								openEditShiftModal={openEditModal}
								openAddShiftModal={openAddModal}
								onShiftDragUpdate={handleShiftDragUpdate}
								allEvents={[
									...calendarShiftEvents,
									...approvedTimeOffEvents,
								]}
								holidays={rawHolidays}
								workplacesData={workplacesData!}
								updateTimeRange={updateTimeRange}
								setCalendarType={setCalendarTypeHandler}
								calendarType={calendarType}
								openRequestsModal={openRequestsModal}
								openAddEventModal={openAddModal}
								openEditEventModal={openEditModal}
								refetch={refetchShifts}
								dragTimeStep={15}
								defaultShiftLengthHours={generalSettings?.defaultShiftLengthHours}
								minShiftLength={generalSettings?.minShiftLength}
								resourcesOverride={calendarResources}
								headerStartContent={
									departments.length > 0 ? (
										<DepartmentFilter
											departments={departments}
											selectedDepartmentIds={selectedDepartmentIds}
											onChange={handleDepartmentFilterChange}
										/>
									) : null
								}
								startOfDay={startOfDay}
								startOfWeek={startOfWeek}
								workplaceWorkingHoursStart={workplaceWorkingHoursStart}
								selectedDate={calendarSelectedDate}
								businessWeekStart={businessWeekStart}
								onEditShift={handleEditShift}
								onTimeOffClick={handleTimeOffClick}
								onDeleteShift={handleDeleteShift}
								onCopyShift={handleCopyShift}
								onCreateShiftByDrag={handleCreateShiftByDrag}
								onPublish={handleOpenPublishModal}
								canManageShifts={canManageShifts}
								canDeleteShifts={canDeleteShifts}
								canManageAllRequests={canManageAllRequests}
								canReorderEmployeeRows={canPersonalizeEmployeeRowOrder}
								scheduleUserId={scheduleUserId}
								isShiftActionsEnabled={isWorkplaceUsersReady}
								isShiftsLoading={isShiftsLoading}
							/>
						) : (
							<DesktopPage
								openEditShiftModal={openEditModal}
								openAddShiftModal={openAddModal}
								onShiftDragUpdate={handleShiftDragUpdate}
								allEvents={[
									...calendarShiftEvents,
									...approvedTimeOffEvents,
								]}
								holidays={rawHolidays}
								workplacesData={workplacesData!}
								updateTimeRange={updateTimeRange}
								setCalendarType={setCalendarTypeHandler}
								calendarType={calendarType}
								openRequestsModal={openRequestsModal}
								openAddEventModal={openAddModal}
								openEditEventModal={openEditModal}
								// reassignShiftToAnotherUser={reassignShiftToAnotherUser}
								refetch={refetchShifts}
								// updateOneShiftHandler={updateOneShiftHandler}
								dragTimeStep={15}
								resourcesOverride={calendarResources}
								// dataForCalendar={dataForCalendar}
								startOfDay={startOfDay}
								startOfWeek={startOfWeek}
								workplaceWorkingHoursStart={workplaceWorkingHoursStart}
								selectedDate={calendarSelectedDate}
								businessWeekStart={businessWeekStart}
								onEditShift={handleEditShift}
								onTimeOffClick={handleTimeOffClick}
								onDeleteShift={handleDeleteShift}
								onCopyShift={handleCopyShift}
								onPublish={handleOpenPublishModal}
								canManageShifts={canManageShifts}
								canDeleteShifts={canDeleteShifts}
								canManageAllRequests={canManageAllRequests}
								scheduleUserId={scheduleUserId}
								isShiftActionsEnabled={isWorkplaceUsersReady}
								isShiftsLoading={isShiftsLoading}
							/>
						)}
					</div>
				</Grid>
			</Grid>
			</section>
			{/* Delete Confirmation Dialog */}
			{isDeleteDialogOpen && (
				<ConfirmDialog
					type="shift"
					isLoading={isDeleteLoading}
					handleDeleteClick={confirmDeleteShift}
					setConfirmDeleteIsOpen={setIsDeleteDialogOpen}
				/>
			)}
			{isDeleteTimeOffDialogOpen && (
				<ConfirmDialog
					type="time-off"
					isLoading={isDeletingTimeOff}
					handleDeleteClick={confirmDeleteTimeOff}
					setConfirmDeleteIsOpen={setIsDeleteTimeOffDialogOpen}
					title={getTimeOffDeleteDialogCopy(
						isCalendarTimeOffOwner(timeOffToDelete, scheduleUserId)
					).title}
					message={
						getTimeOffDeleteDialogCopy(
							isCalendarTimeOffOwner(timeOffToDelete, scheduleUserId)
						).message
					}
				/>
			)}
			{/* Delete Employee Request Confirmation Dialog */}
			<Dialog
				open={deleteRequestConfirmOpen}
				onClose={() => setDeleteRequestConfirmOpen(false)}
				container={fullscreenContainer}
			>
				<DialogTitle>Delete Request</DialogTitle>
				<DialogContent>
					<MuiTypography>Are you sure you want to delete this employee request?</MuiTypography>
				</DialogContent>
				<DialogActions>
					<MuiButton onClick={() => setDeleteRequestConfirmOpen(false)}>Cancel</MuiButton>
					<MuiButton onClick={confirmDeleteRequest} color="error" disabled={isDeletingRequest}>
						{isDeletingRequest ? 'Deleting...' : 'Delete'}
					</MuiButton>
				</DialogActions>
			</Dialog>
			{openNoShiftsPublishDialog && (
				<NoShiftsToPublishDialog onClose={() => setOpenNoShiftsPublishDialog(false)} />
			)}
			{openPublishModal && (
				<PublishShiftsModal
					workplaceId={workplaceId}
					initialStartDate={fromDate}
					initialEndDate={toDate}
					onPublish={handlePublishShifts}
					onClose={() => setOpenPublishModal(false)}
				/>
			)}
			<ScheduleEventDetailDialog
				open={isEventDialogOpen}
				event={selectedEvent}
				eventTypeLabel={
					selectedEvent ? _eventTypesMap[selectedEvent.type]?.label || 'Event' : 'Event'
				}
				timezone={timezones}
				container={fullscreenContainer}
				onClose={handleCloseEventDialog}
			/>
			<ShiftSchedulerDialog
				open={shiftSchedulerDialog.open}
				mode={shiftSchedulerDialog.mode}
				shift={shiftSchedulerDialog.shift}
				createContext={shiftSchedulerDialog.createContext}
				employees={workplaceUsers || []}
				jobCodes={jobCodesData || []}
				positions={positions}
				minShiftLength={generalSettings?.minShiftLength}
				maxShiftLength={generalSettings?.maxShiftLength}
				generalSettings={generalSettings}
				daysData={allDays}
				weekShifts={allShifts}
				weekStartDate={fromDate}
				weekEndDate={toDate}
				isSaving={isQuickEditSaving || isCreateShiftLoading}
				onClose={closeShiftSchedulerDialog}
				onSaveDayUser={handleQuickSaveDayUser}
				onSaveDayPosition={handleQuickSaveDayPosition}
				onSaveWeekFields={handleQuickSaveWeekFields}
				onCreateDay={handleCreateDayShift}
				onCreateWeek={handleCreateWeekShift}
				onOpenFullEdit={handleOpenFullEditFromDialog}
			/>
			<TimeOffDetailDialog
				open={!!selectedTimeOffRequest}
				request={selectedTimeOffRequest}
				container={fullscreenContainer}
				onClose={() => setSelectedTimeOffRequest(null)}
			/>
			<Dialog
				open={!!selectedRequestForDetail}
				onClose={handleCloseRequestDetail}
				maxWidth="md"
				fullWidth
				fullScreen={isRequestDetailMobile}
				container={fullscreenContainer}
				aria-labelledby="employee-request-detail-title"
				PaperProps={{
					sx: {
						borderRadius: isRequestDetailMobile ? 0 : 2,
						maxHeight: isRequestDetailMobile ? '100%' : '90vh',
					},
				}}
			>
				<DialogContent sx={{ p: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
					{selectedRequestForDetail && (
						<RequestDetailView
							request={selectedRequestForDetail}
							onApprove={handleApproveRequestFromDetail}
							onDeny={handleDenyRequestFromDetail}
							onClose={handleCloseRequestDetail}
							canManageRequests={canManageAllRequests}
							allShifts={allShifts}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
